import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import PdfPreviewModal from './components/PdfPreviewModal';
import InternalNotesPanel from './components/InternalNotesPanel';
import { AutoSaveIndicator } from './hooks/useAutoSave';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
export { fmt as fmtNumber };

// Calcul honoraires RealEase identique au backend
const calcRealEaseFee = (price) => {
  if (price <= 100000) return { rate: 10, amount: price * 0.10 };
  if (price <= 200000) return { rate: 9, amount: price * 0.09 };
  if (price <= 300000) return { rate: 8.33, amount: price * 0.0833 };
  if (price <= 400000) return { rate: 7.5, amount: price * 0.075 };
  if (price <= 500000) return { rate: 7, amount: price * 0.07 };
  return { rate: 6.67, amount: price * 0.0667 };
};

// Mensualite pret amortissable + assurance sur capital initial
const calcMonthlyPayment = (loan, ratePct, durationY, insurancePct) => {
  if (loan <= 0 || durationY <= 0) return { capital: 0, insurance: 0, total: 0 };
  const n = durationY * 12;
  const mr = ratePct / 100 / 12;
  const cap = mr === 0 ? loan / n : loan * (mr / (1 - Math.pow(1 + mr, -n)));
  const ins = (loan * (insurancePct / 100)) / 12;
  return { capital: cap, insurance: ins, total: cap + ins };
};

export const unitMonthlyRent = (u) => {
  if (!u) return 0;
  if (u.short_term) {
    const base = (+u.nightly_rate || 0) * 30.4 * (+u.occupancy_rate || 0) / 100;
    if (u.seasonal) {
      const hsM = Math.min(12, Math.max(0, +u.hs_months || 0));
      const hs = (+u.hs_nightly_rate || 0) * 30.4 * (+u.hs_occupancy_rate || 0) / 100;
      return Math.round(((hsM * hs + (12 - hsM) * base) / 12) * 100) / 100;
    }
    return Math.round(base * 100) / 100;
  }
  return +u.monthly_rent || 0;
};

export const computeAnalysisLive = (form) => {
  const target = +form.target_price || 0;
  const asking = +form.asking_price || 0;
  const property_type = form.property_type || 'ancien';
  const notaireFees = form.notaire_fees != null && form.notaire_fees !== ''
    ? +form.notaire_fees
    : target * 0.075;
  const worksItems = Array.isArray(form.works_items) ? form.works_items : [];
  const worksItemsTotal = worksItems.reduce((s, it) => s + (+it.amount || 0), 0);
  const worksBudget = worksItems.length > 0 ? worksItemsTotal : (+form.works_budget || 0);
  const enedisFees = +form.enedis_fees || 0;
  const waterFees = +form.water_fees || 0;
  // Compatibilite : si les champs detailles sont saisis, on les utilise. Sinon on retombe sur le legacy.
  const enedis = (enedisFees || waterFees) ? (enedisFees + waterFees) : (+form.enedis_water_fees || 0);
  const furniture = +form.furniture_budget || 0;
  const customExpensesTotal = (form.custom_expenses || []).reduce((s, ce) => s + (+ce.amount || 0), 0);

  // Honoraires RealEase calcules sur (prix + travaux + ENEDIS/eau + mobilier + custom)
  // Excluent : frais notaire + frais agence
  const realeaseBase = target + worksBudget + enedis + furniture + customExpensesTotal;
  const re = calcRealEaseFee(realeaseBase);
  const feeAuto = { ...re };
  const feeOverride = form.realease_fee_override;
  const feeOverridden = feeOverride !== null && feeOverride !== undefined && feeOverride !== '' && +feeOverride >= 0;
  if (feeOverridden) {
    re.amount = Math.round(+feeOverride * 100) / 100;
    re.rate = realeaseBase > 0 ? Math.round((+feeOverride / realeaseBase) * 10000) / 100 : 0;
  }

  const includeFees = !!form.include_realease_fees_in_works;
  const worksEnvelopeBank = worksBudget + enedis + furniture + (includeFees ? re.amount : 0);
  const totalOp = target + notaireFees + (+form.agency_fees || 0)
    + worksBudget + enedis + furniture + re.amount + customExpensesTotal;
  const fin = form.financing || {};
  const down = +fin.down_payment || 0;
  let loan = Math.max(totalOp - down, 0);
  // Revente de terrain : n'impacte pas les honoraires RealEase
  const landSaleEnabled = !!form.land_sale_enabled;
  const landSale = landSaleEnabled ? (+form.land_sale_price || 0) : 0;
  const landSaleUse = form.land_sale_use || 'tresorerie';
  if (landSale > 0 && landSaleUse === 'remboursement') loan = Math.max(loan - landSale, 0);
  const dur = +fin.loan_duration_years || 25;
  const ir = +fin.interest_rate || 3.65;
  const insR = +fin.insurance_rate || 0.36;
  const m = calcMonthlyPayment(loan, ir, dur, insR);
  const m20 = calcMonthlyPayment(loan, ir, 20, insR);
  const m25 = calcMonthlyPayment(loan, ir, 25, insR);
  const unitsRentTotal = (form.rental_units || []).reduce((s, u) => s + unitMonthlyRent(u), 0);
  const unitsCurrentRentTotal = (form.rental_units || []).reduce((s, u) => s + (+u.current_monthly_rent || 0), 0);
  const effectiveMonthlyRent = unitsRentTotal > 0 ? unitsRentTotal : (+form.monthly_rent || 0);
  const effectiveCurrentMonthlyRent = unitsCurrentRentTotal > 0 ? unitsCurrentRentTotal : (+form.current_monthly_rent || 0);
  const annualRent = (effectiveMonthlyRent + (+form.other_income || 0)) * 12;
  const currentAnnualRent = (effectiveCurrentMonthlyRent + (+form.other_income || 0)) * 12;
  const pmRate = +form.property_management_rate || 7;
  // Gestion locative : base = loyers longue duree uniquement (courte duree geree a part)
  const ltUnitsRentTotal = (form.rental_units || []).filter(u => u && !u.short_term).reduce((s, u) => s + unitMonthlyRent(u), 0);
  const pmBaseMonthly = unitsRentTotal > 0 ? ltUnitsRentTotal : (+form.monthly_rent || 0);
  const propertyMgmtAnnual = form.enable_property_management ? Math.round((pmBaseMonthly + (+form.other_income || 0)) * 12 * pmRate / 100) : 0;
  const currentPropertyMgmtAnnual = form.enable_property_management ? Math.round(currentAnnualRent * pmRate / 100) : 0;
  // Iter 96 : charges custom (liste {label, amount}) en plus du champ legacy annual_other_charges.
  const customAnnualCharges = Array.isArray(form.custom_annual_charges) ? form.custom_annual_charges : [];
  const customAnnualChargesTotal = customAnnualCharges.reduce((s, c) => s + (+c.amount || 0), 0);
  const annualCharges = (+form.annual_taxes || 0) + (+form.annual_insurance || 0)
    + (+form.annual_accounting || 0) + (+form.annual_other_charges || 0)
    + customAnnualChargesTotal
    + propertyMgmtAnnual;
  const currentAnnualCharges = (+form.annual_taxes || 0) + (+form.annual_insurance || 0)
    + (+form.annual_accounting || 0) + (+form.annual_other_charges || 0)
    + customAnnualChargesTotal
    + currentPropertyMgmtAnnual;
  // Iter 96 : mensualite overridable (credit reellement obtenu). Si renseigne > 0,
  // remplace m.total et impacte directement l'annualLoan + cashflow.
  const monthlyOverride = +form.monthly_payment_override || 0;
  const effectiveMonthly = monthlyOverride > 0 ? monthlyOverride : m.total;
  const annualLoan = effectiveMonthly * 12;
  const annualCF = annualRent - annualCharges - annualLoan;
  const annualCF20 = annualRent - annualCharges - (m20.total * 12);
  const annualCF25 = annualRent - annualCharges - (m25.total * 12);
  const yieldBase = landSale > 0 ? Math.max(totalOp - landSale, 0) : totalOp;
  const grossYield = yieldBase ? (annualRent / yieldBase * 100) : 0;
  const netYield = yieldBase ? ((annualRent - annualCharges) / yieldBase * 100) : 0;
  // Scenario actuel
  const hasCurrentRent = effectiveCurrentMonthlyRent > 0;
  const currentAnnualCF = hasCurrentRent ? (currentAnnualRent - currentAnnualCharges - annualLoan) : 0;
  const currentGrossYield = (hasCurrentRent && yieldBase) ? (currentAnnualRent / yieldBase * 100) : 0;
  const currentNetYield = (hasCurrentRent && yieldBase) ? ((currentAnnualRent - currentAnnualCharges) / yieldBase * 100) : 0;
  const ppm = form.surface ? (target / +form.surface) : 0;
  const negoPct = asking ? ((asking - target) / asking * 100) : 0;
  return {
    notaireFeesAuto: notaireFees,
    realeaseFee: re,
    realeaseFeeAuto: feeAuto,
    realeaseFeeOverridden: feeOverridden,
    realeaseBase,
    totalOperation: totalOp,
    worksBudget,
    worksItemsTotal,
    worksEnvelopeBank,
    enedis,
    furniture,
    loanAmount: loan,
    monthly: m,
    monthly20: m20,
    monthly25: m25,
    annualRent,
    unitsRentTotal,
    unitsCurrentRentTotal,
    effectiveCurrentMonthlyRent,
    currentAnnualRent,
    propertyMgmtAnnual,
    pmRate,
    customAnnualCharges,
    customAnnualChargesTotal,
    annualCharges,
    annualLoanPayment: annualLoan,
    effectiveMonthly,
    monthlyOverrideActive: monthlyOverride > 0,
    annualCashflow: annualCF,
    monthlyCashflow: annualCF / 12,
    annualCashflow20: annualCF20,
    monthlyCashflow20: annualCF20 / 12,
    annualCashflow25: annualCF25,
    monthlyCashflow25: annualCF25 / 12,
    grossYield,
    netYield,
    landSaleEnabled,
    landSalePrice: landSale,
    landSaleUse,
    netOperationAfterLandSale: yieldBase,
    hasCurrentRent,
    currentAnnualCashflow: currentAnnualCF,
    currentMonthlyCashflow: currentAnnualCF / 12,
    currentGrossYield,
    currentNetYield,
    pricePerSqm: ppm,
    negotiationPct: negoPct,
  };
};

// ==================== MENU FLOTTANT : DIFF ENTRE SCENARIOS ====================
export const ScenarioDiffFloat = ({ form, onBeforeOpen }) => {
  const [open, setOpen] = useState(false);
  const scs = getRentScenarios(form);
  const activeId = getActiveScenarioId(form, scs);
  const cols = useMemo(() => scs.map(sc => {
    const data = sc.id === activeId ? form : applyScenarioFields(form, sc);
    return { id: sc.id, name: sc.name, isActive: sc.id === activeId, c: computeAnalysisLive(data) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [form]);
  const single = scs.length < 2;

  useEffect(() => {
    const h = () => { if (onBeforeOpen) onBeforeOpen(); setOpen(true); };
    window.addEventListener('open-scenario-diff', h);
    return () => window.removeEventListener('open-scenario-diff', h);
  }, [onBeforeOpen]);
  const dur = +(form.financing?.loan_duration_years) || 25;
  const metrics = [
    { l: 'Budget travaux', f: c => `${fmt(c.worksBudget)} EUR`, v: c => -c.worksBudget },
    { l: 'Coût total opération', f: c => `${fmt(c.totalOperation)} EUR`, v: c => -c.totalOperation },
    { l: 'Mensualité crédit', f: c => `${fmt(c.monthly.total)} EUR`, v: c => -c.monthly.total },
    { l: 'Revenus / mois', f: c => `${fmt(c.annualRent / 12)} EUR`, v: c => c.annualRent },
    { l: 'Revenus / an', f: c => `${fmt(c.annualRent)} EUR`, v: c => c.annualRent },
    { l: 'Charges / an', f: c => `${fmt(c.annualCharges)} EUR`, v: c => -c.annualCharges },
    { l: 'Rendement brut', f: c => `${c.grossYield.toFixed(2)} %`, v: c => c.grossYield },
    { l: 'Rendement net', f: c => `${c.netYield.toFixed(2)} %`, v: c => c.netYield },
    { l: `Cashflow / mois (${dur} ans)`, f: c => `${c.monthlyCashflow >= 0 ? '+' : ''}${fmt(c.monthlyCashflow)} EUR`, v: c => c.monthlyCashflow },
    { l: 'Cashflow / an', f: c => `${c.annualCashflow >= 0 ? '+' : ''}${fmt(c.annualCashflow)} EUR`, v: c => c.annualCashflow },
  ];
  return (
    <>
      <button onClick={() => { if (!open && onBeforeOpen) onBeforeOpen(); setOpen(o => !o); }}
              className={`fixed right-0 top-[38%] z-40 rounded-l-xl px-3 py-5 shadow-2xl font-bold text-sm transition-colors ring-2 ring-white/40 ${open ? 'bg-[#0F5C57] text-white' : 'bg-[#008A8D] text-white hover:bg-[#0F5C57]'}`}
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
              title="Comparer les scénarios de revenus"
              data-testid="scenario-diff-toggle">
        ⇆ Diff scénarios ({scs.length})
      </button>
      {open && (
        <div className="fixed right-12 top-[20%] z-40 w-[min(92vw,520px)] max-h-[65vh] overflow-auto bg-white/95 backdrop-blur rounded-xl shadow-2xl border border-[#008A8D]/25"
             data-testid="scenario-diff-panel">
          <div className="sticky top-0 bg-[#008A8D] text-white px-4 py-2.5 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold leading-tight">Diff des scénarios</p>
              <p className="text-[10px] text-white/75">Loyers, charges &amp; rentabilité — mise à jour en direct</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white text-lg leading-none" data-testid="scenario-diff-close">×</button>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-2 text-[10px] uppercase text-gray-400 font-semibold">Indicateur</th>
                {cols.map((col, i) => (
                  <th key={col.id} className="text-right p-2 align-top" data-testid={`scenario-diff-col-${i}`}>
                    <span className={`font-bold ${col.isActive ? 'text-[#008A8D]' : 'text-gray-700'}`}>{col.name}</span>
                    {col.isActive && <span className="block text-[9px] text-[#008A8D] font-semibold">actif</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((mrow, ri) => {
                const best = Math.max(...cols.map(col => mrow.v(col.c)));
                return (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-gray-50/60' : ''}>
                    <td className="p-2 text-gray-500">{mrow.l}</td>
                    {cols.map(col => {
                      const isBest = !single && mrow.v(col.c) === best;
                      return (
                        <td key={col.id} className={`p-2 text-right whitespace-nowrap ${isBest ? 'font-bold text-emerald-600' : col.isActive ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                          {mrow.f(col.c)}{isBest ? ' ●' : ''}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-3 py-2 text-[10px] text-gray-400 border-t border-gray-100">
            {single
              ? 'Un seul scénario pour l\'instant — ajoutez-en un via « + Scénario » dans la section Loyers de l\'analyse pour comparer.'
              : '● = meilleure valeur. Chaque scénario a ses travaux, loyers et charges — honoraires, emprunt et rentabilité recalculés.'}
          </p>
        </div>
      )}
    </>
  );
};

// ==================== LISTE ====================
export const AnalysesPage = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [search, setSearch] = useState('');
  const [renaming, setRenaming] = useState(null); // { analysis_id, name }
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/analyses`);
      setAnalyses(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/analyses`, {
        name: newName.trim(),
        property_type: 'ancien',
        asking_price: 0, target_price: 0,
        financing: { down_payment: 0, loan_duration_years: 25, interest_rate: 3.65, insurance_rate: 0.36 },
      });
      window.location.href = `/analyses/${r.data.analysis_id}`;
    } catch (e) {
      alert('Erreur creation : ' + (e.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/analyses/${id}/duplicate`);
      fetchAll();
    } catch (e) { alert('Erreur duplication'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette analyse ?')) return;
    try {
      await axios.delete(`${API}/analyses/${id}`);
      fetchAll();
    } catch (e) { alert('Erreur suppression'); }
  };

  const openRename = (a) => {
    setRenaming(a);
    setRenameValue(a.name || '');
  };

  const submitRename = async () => {
    if (!renaming) return;
    const next = renameValue.trim();
    if (!next || next === renaming.name) {
      setRenaming(null);
      return;
    }
    setRenameSaving(true);
    try {
      await axios.put(`${API}/analyses/${renaming.analysis_id}`, { name: next });
      setRenaming(null);
      fetchAll();
    } catch (e) {
      alert('Erreur renommage : ' + (e.response?.data?.detail || e.message));
    } finally {
      setRenameSaving(false);
    }
  };

  // Filtrage local (nom / client / ville)
  const normalizedSearch = search.trim().toLowerCase();
  const filteredAnalyses = !normalizedSearch
    ? analyses
    : analyses.filter(a => {
        const haystack = [
          a.name, a.client_name, a.city, a.address,
          a.property_type, a.analysis_id,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalizedSearch);
      });

  // Telecharge la derniere version du dossier de presentation (toujours regenere par le backend)
  const handleQuickDownload = async (a, kind = 'full') => {
    try {
      const ep = kind === 'synthesis' ? 'synthesis-pdf' : 'pdf';
      // Cache-bust : ajoute un timestamp pour forcer le navigateur a refaire l'appel
      const ts = Date.now();
      const r = await axios.get(`${API}/analyses/${a.analysis_id}/${ep}?_=${ts}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const prefix = kind === 'synthesis' ? 'Synthese' : 'Analyse';
      link.download = `RealEase_${prefix}_${(a.name || a.analysis_id).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message || 'Erreur inconnue';
      if (e.response?.data instanceof Blob) {
        try { const t = await e.response.data.text(); try { detail = JSON.parse(t).detail || t; } catch { detail = t; } } catch {}
      } else if (e.response?.data?.detail) detail = e.response.data.detail;
      alert('Erreur telechargement :\n\n' + detail);
    }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="analyses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analyse projets</h1>
          <p className="text-gray-600 mt-1">Calculatrice de rentabilite + generation de PDF detaille pour vos clients.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="realease-btn-primary" data-testid="create-analysis-btn">
          + Nouvelle analyse
        </button>
      </div>

      <div className="bg-[#13736D]/5 border border-[#13736D]/20 rounded-lg p-3 mb-4 flex items-start gap-3 text-sm">
        <svg className="w-5 h-5 text-[#13736D] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <div>
          <span className="font-semibold text-[#13736D]">Toujours la derniere version :</span>{' '}
          <span className="text-gray-700">les boutons <b>PDF dossier</b> et <b>Synthese</b> regenerent le PDF a chaque clic avec la mise en page la plus recente. Pour pousser la nouvelle version au client, ouvrez l'analyse puis cliquez "Envoyer au client".</span>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.85-5.65a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, client, ville..."
            className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#008A8D] focus:outline-none focus:ring-1 focus:ring-[#008A8D]"
            data-testid="analyses-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-5 h-5 rounded-full flex items-center justify-center hover:bg-gray-100"
              title="Effacer"
              data-testid="analyses-search-clear">
              &times;
            </button>
          )}
        </div>
        {search && (
          <span className="text-xs text-gray-500" data-testid="analyses-search-count">
            {filteredAnalyses.length} resultat{filteredAnalyses.length > 1 ? 's' : ''} sur {analyses.length}
          </span>
        )}
      </div>

      {loading ? <div className="text-gray-500">Chargement...</div> : (
        analyses.length === 0 ? (
          <div className="realease-card p-12 text-center">
            <p className="text-gray-600 mb-4">Aucune analyse pour le moment.</p>
            <button onClick={() => setShowCreate(true)} className="realease-btn-primary">Creer ma premiere analyse</button>
          </div>
        ) : filteredAnalyses.length === 0 ? (
          <div className="realease-card p-12 text-center" data-testid="analyses-empty-search">
            <p className="text-gray-600 mb-2">Aucune analyse ne correspond a <b>"{search}"</b>.</p>
            <button onClick={() => setSearch('')} className="text-[#008A8D] hover:underline text-sm">Effacer la recherche</button>
          </div>
        ) : (
          <div className="realease-card overflow-hidden">
            <table className="w-full" data-testid="analyses-table">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="p-3">Nom</th>
                  <th className="p-3">Client</th>
                  <th className="p-3">Ville</th>
                  <th className="p-3">Prix cible</th>
                  <th className="p-3">Cout total</th>
                  <th className="p-3">Cashflow / mois (20 ans)</th>
                  <th className="p-3">Rendement net</th>
                  <th className="p-3">Statut PDF</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAnalyses.map(a => {
                  const c = a.computed || {};
                  const cf = (c.monthly_cashflow_20y !== undefined ? c.monthly_cashflow_20y : c.monthly_cashflow) || 0;
                  return (
                    <tr key={a.analysis_id} className="hover:bg-gray-50" data-testid={`analysis-row-${a.analysis_id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <a href={`/analyses/${a.analysis_id}`} className="font-medium text-[#008A8D] hover:underline">{a.name}</a>
                          <button
                            onClick={(e) => { e.preventDefault(); openRename(a); }}
                            className="text-gray-400 hover:text-[#008A8D] p-1 rounded hover:bg-gray-100"
                            title="Renommer l'analyse"
                            data-testid={`rename-analysis-${a.analysis_id}`}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                        {a.created_by_name && (
                          <p className="text-[11px] text-gray-400 mt-0.5" data-testid={`analysis-creator-${a.analysis_id}`}>
                            Initiée par <span className="font-medium text-gray-500">{a.created_by_name}</span>{a.created_at ? ` · ${a.created_at.slice(0, 10).split('-').reverse().join('/')}` : ''}
                          </p>
                        )}
                      </td>
                      <td className="p-3 text-sm">{a.client_name || '—'}</td>
                      <td className="p-3 text-sm">{a.city || '—'}</td>
                      <td className="p-3 text-sm">{fmt(a.target_price)} EUR</td>
                      <td className="p-3 text-sm font-medium">{fmt(c.total_operation)} EUR</td>
                      <td className={`p-3 text-sm font-medium ${cf >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {cf >= 0 ? '+' : ''}{fmt(cf)} EUR
                      </td>
                      <td className="p-3 text-sm">{(c.net_yield_pct || 0).toFixed(2)} %</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          a.pdf_status === 'sent_client' ? 'bg-green-100 text-green-700' :
                          a.pdf_status === 'sent_drive' ? 'bg-blue-100 text-blue-700' :
                          a.pdf_status === 'downloaded' ? 'bg-gray-100 text-gray-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {a.pdf_status === 'sent_client' ? 'Envoye client' :
                           a.pdf_status === 'sent_drive' ? 'Sur Drive' :
                           a.pdf_status === 'downloaded' ? 'Telecharge' : 'Brouillon'}
                        </span>
                      </td>
                      <td className="p-3 text-sm flex gap-2">
                        <button onClick={() => handleQuickDownload(a, 'full')}
                                className="text-[#008A8D] hover:bg-[#008A8D]/10 px-2 py-1 rounded font-medium text-xs"
                                title="Regenerer et telecharger le dossier de presentation (derniere version)"
                                data-testid={`quick-download-${a.analysis_id}`}>
                          PDF dossier
                          {a.pdf_engine_last === 'reportlab' && (
                            <span className="ml-1 text-[9px] px-1 py-0.5 bg-amber-100 text-amber-800 rounded font-bold" title="Derniere generation via fallback ReportLab (mise en page degraded). Cliquez pour regenerer.">
                              v.simple
                            </span>
                          )}
                        </button>
                        <button onClick={() => handleQuickDownload(a, 'synthesis')}
                                className="text-[#13736D] hover:bg-[#13736D]/10 px-2 py-1 rounded font-medium text-xs"
                                title="Regenerer et telecharger la synthese 1 page banquier"
                                data-testid={`quick-synthesis-${a.analysis_id}`}>
                          Synthese
                        </button>
                        <button onClick={() => handleDuplicate(a.analysis_id)} className="text-gray-600 hover:text-[#008A8D]" title="Dupliquer" data-testid={`duplicate-${a.analysis_id}`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                        <button onClick={() => handleDelete(a.analysis_id)} className="text-gray-600 hover:text-red-600" title="Supprimer">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="create-analysis-modal">
            <h3 className="text-xl font-bold mb-4">Nouvelle analyse</h3>
            <label className="realease-label">Nom de l'analyse</label>
            <input
              type="text" autoFocus value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Ex: Maison Carhaix-Plouguer"
              className="realease-input mb-4"
              data-testid="new-analysis-name"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border rounded-lg">Annuler</button>
              <button onClick={handleCreate} disabled={creating || !newName.trim()} className="realease-btn-primary disabled:opacity-50" data-testid="confirm-create-analysis">
                {creating ? 'Creation...' : 'Creer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal renommage */}
      {renaming && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="rename-analysis-modal">
            <h3 className="text-xl font-bold mb-1">Renommer l'analyse</h3>
            <p className="text-xs text-gray-500 mb-4">Nom actuel : <b>{renaming.name}</b></p>
            <label className="realease-label">Nouveau nom</label>
            <input
              type="text" autoFocus value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(null); }}
              placeholder="Ex: Maison Carhaix-Plouguer - version banque"
              className="realease-input mb-4"
              data-testid="rename-analysis-input"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRenaming(null)} className="px-4 py-2 border rounded-lg" data-testid="rename-analysis-cancel">Annuler</button>
              <button
                onClick={submitRename}
                disabled={renameSaving || !renameValue.trim() || renameValue.trim() === renaming.name}
                className="realease-btn-primary disabled:opacity-50"
                data-testid="rename-analysis-confirm">
                {renameSaving ? 'Enregistrement...' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== DETAIL / FORMULAIRE ====================
export const AnalysisDetailPage = () => {
  const id = window.location.pathname.split('/').pop();
  const [analysis, setAnalysis] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [sendMode, setSendMode] = useState('client'); // 'client' | 'drive'
  const [projects, setProjects] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [autoSaveError, setAutoSaveError] = useState(null);

  // Refs pour le moteur d'auto-save (debounce + anti-collision)
  const lastSavedJsonRef = useRef(null);
  const saveTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const formRef = useRef(null);

  const stripVolatile = (obj) => {
    if (!obj) return null;
    const { computed, created_at, updated_at, created_by, analysis_id, ...rest } = obj;
    return rest;
  };

  const fetchAnalysis = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/analyses/${id}`);
      setAnalysis(r.data);
      const initial = {
        ...r.data,
        financing: r.data.financing || { down_payment: 0, loan_duration_years: 25, interest_rate: 3.65, insurance_rate: 0.36 },
      };
      setForm(initial);
      // Memorise l'etat charge pour eviter un auto-save inutile au premier render
      lastSavedJsonRef.current = JSON.stringify(stripVolatile(initial));
      setAutoSaveStatus('saved');
      setSavedAt(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);
  useEffect(() => {
    axios.get(`${API}/projects`).then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  // Garde une ref a jour du form pour pouvoir l'inclure dans la closure du timer
  useEffect(() => { formRef.current = form; }, [form]);

  // Auto-save debounce 1.2s : toute modification du form declenche un PUT silencieux
  useEffect(() => {
    if (!form) return;
    const currentJson = JSON.stringify(stripVolatile(form));
    if (lastSavedJsonRef.current === null) {
      lastSavedJsonRef.current = currentJson;
      return;
    }
    if (currentJson === lastSavedJsonRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setAutoSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const snapshot = formRef.current;
        const payload = stripVolatile(snapshot);
        const r = await axios.put(`${API}/analyses/${id}`, payload);
        setAnalysis(r.data);
        lastSavedJsonRef.current = JSON.stringify(stripVolatile(snapshot));
        setAutoSaveStatus('saved');
        setSavedAt(new Date());
        setAutoSaveError(null);
      } catch (e) {
        setAutoSaveStatus('error');
        setAutoSaveError(e.response?.data?.detail || e.message);
      } finally {
        inFlightRef.current = false;
      }
    }, 1200);

    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [form, id]);

  const live = useMemo(() => form ? computeAnalysisLive(form) : null, [form]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFin = (k, v) => setForm(f => ({ ...f, financing: { ...(f.financing || {}), [k]: v } }));

  const save = async (silent = false) => {
    if (!form) return;
    setSaving(true);
    if (silent) setAutoSaveStatus('saving');
    try {
      const payload = stripVolatile(form);
      const r = await axios.put(`${API}/analyses/${id}`, payload);
      setAnalysis(r.data);
      lastSavedJsonRef.current = JSON.stringify(stripVolatile(form));
      setSavedAt(new Date());
      setAutoSaveStatus('saved');
      setAutoSaveError(null);
    } catch (e) {
      if (silent) {
        setAutoSaveStatus('error');
        setAutoSaveError(e.response?.data?.detail || e.message);
      } else {
        alert('Erreur sauvegarde : ' + (e.response?.data?.detail || e.message));
      }
    } finally { setSaving(false); }
  };

  const downloadPdf = async (engine = 'auto') => {
    try {
      await save(true);
      const url_q = engine === 'fallback' ? '?engine=fallback&_=' + Date.now() : '?_=' + Date.now();
      const r = await axios.get(`${API}/analyses/${id}/pdf${url_q}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const suffix = engine === 'fallback' ? '_apercu' : '';
      a.download = `RealEase_Analyse_${(analysis.name || id).replace(/\s+/g, '_')}${suffix}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      fetchAnalysis();
    } catch (e) {
      // L'erreur arrive en Blob quand responseType=blob : il faut la lire manuellement
      let detail = e.message || 'Erreur inconnue';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || text; } catch { detail = text; }
        } catch {}
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      console.error('PDF download failed:', detail, e);
      alert('Erreur telechargement du PDF :\n\n' + detail);
    }
  };

  const downloadSynthesis = async () => { // eslint-disable-line no-unused-vars
    try {
      await save(true);
      const ts = Date.now();
      const r = await axios.get(`${API}/analyses/${id}/synthesis-pdf?_=${ts}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `RealEase_Synthese_${(analysis.name || id).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message || 'Erreur inconnue';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || text; } catch { detail = text; }
        } catch {}
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      alert('Erreur telechargement de la synthese :\n\n' + detail);
    }
  };

  if (loading || !form) return <div className="p-8 text-gray-500">Chargement...</div>;

  const loanYears = form?.financing?.loan_duration_years || 25;
  // Cashflow base sur la duree de pret choisie par l'utilisateur (et non plus 20 ans force)
  const cf = live?.monthlyCashflow ?? 0;
  const cfColor = cf >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="p-8 space-y-6 max-w-7xl" data-testid="analysis-detail">
      <a href="/analyses" className="text-[#008A8D] hover:underline text-sm inline-block">&larr; Retour aux analyses</a>

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <input
            type="text" value={form.name || ''}
            onChange={e => set('name', e.target.value)}
            className="text-3xl font-bold text-gray-900 bg-transparent border-b border-transparent focus:border-gray-300 focus:outline-none w-full"
            data-testid="analysis-name-input"
          />
          <div className="mt-1 flex items-center gap-3" data-testid="analysis-autosave-indicator">
            <AutoSaveIndicator status={autoSaveStatus} savedAt={savedAt} error={autoSaveError} />
            {analysis?.created_by_name && (
              <span className="text-xs text-gray-400" data-testid="analysis-detail-creator">
                Initiée par <b className="text-gray-500 font-medium">{analysis.created_by_name}</b>
                {analysis.created_at ? ` le ${analysis.created_at.slice(0, 10).split('-').reverse().join('/')}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <ConvertToProjectBtn analysisId={id} currentProjectId={form.project_id} />
          <button onClick={() => downloadPdf('auto')} className="realease-btn-primary" data-testid="download-pdf-btn">
            Telecharger PDF
          </button>
          <button onClick={() => { setSendMode('client'); setShowSendModal(true); }} className="px-4 py-2 bg-[#E8DCC4] text-[#1A1A1A] rounded-lg hover:bg-[#dcc9a8] font-semibold" data-testid="send-client-btn">
            Envoyer au client
          </button>
          <button onClick={() => setShowBankModal(true)}
                  className="px-4 py-2 bg-[#13736D] text-white rounded-lg hover:bg-[#0F5C57] font-semibold flex items-center gap-1.5"
                  title="Dossier de financement banque : presentation complete recalculee avec forfait travaux custom (honoraires englobes)"
                  data-testid="download-synthesis-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Dossier banque
          </button>
        </div>
      </div>

      <ScenarioDiffFloat form={form} />

      {/* Layout 2 colonnes : main (gauche) + recap sticky (droite) */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0 space-y-6">

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analysis-kpis">
        <div className="realease-card p-4">
          <p className="text-xs text-gray-500">Cout total operation</p>
          <p className="text-2xl font-bold text-[#008A8D]">{fmt(live.totalOperation)} EUR</p>
        </div>
        <div className="realease-card p-4">
          <p className="text-xs text-gray-500">Cashflow mensuel ({loanYears} ans)</p>
          <p className={`text-2xl font-bold ${cfColor}`}>{cf >= 0 ? '+' : ''}{fmt(cf)} EUR</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Sur 20 ans : {(live?.monthlyCashflow20 ?? 0) >= 0 ? '+' : ''}{fmt(live?.monthlyCashflow20 ?? 0)} EUR &middot; 25 ans : {(live?.monthlyCashflow25 ?? 0) >= 0 ? '+' : ''}{fmt(live?.monthlyCashflow25 ?? 0)} EUR</p>
        </div>
        <div className="realease-card p-4">
          <p className="text-xs text-gray-500">Rendement net (apres charges)</p>
          <p className="text-2xl font-bold text-[#008A8D]" data-testid="net-yield">{(live.netYield).toFixed(2)} %</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Hors mensualite pret</p>
        </div>
        <div className="realease-card p-4">
          <p className="text-xs text-gray-500">Mensualite (avec assurance)</p>
          <p className="text-2xl font-bold text-[#008A8D]">{fmt(live.monthly.total)} EUR</p>
        </div>
      </div>

      {/* Recap rapide a droite (deplace plus bas dans le layout 2 colonnes) */}

      {/* Ligne 1 : Le bien + Prix (2 colonnes) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bien */}
        <div className="realease-card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Le bien</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="realease-label">Client</label><input type="text" value={form.client_name || ''} onChange={e => set('client_name', e.target.value)} className="realease-input" data-testid="client-name" /></div>
            <div><label className="realease-label">Type</label>
              <select value={form.property_type || 'ancien'} onChange={e => set('property_type', e.target.value)} className="realease-input">
                <option value="ancien">Ancien (frais notaire 7,5 %)</option>
                <option value="neuf">Neuf (frais notaire 7,5 %)</option>
              </select>
            </div>
            <div><label className="realease-label">Ville</label><input type="text" value={form.city || ''} onChange={e => set('city', e.target.value)} className="realease-input" data-testid="city-input" /></div>
            <div><label className="realease-label">Code postal</label><input type="text" value={form.postal_code || ''} onChange={e => set('postal_code', e.target.value)} className="realease-input" /></div>
            <div><label className="realease-label">Surface (m²)</label><input type="number" value={form.surface || ''} onChange={e => set('surface', +e.target.value || 0)} className="realease-input" /></div>
            <div><label className="realease-label">Prix au m²</label><p className="realease-input bg-gray-50 text-gray-600">{live.pricePerSqm ? fmt(live.pricePerSqm) + ' EUR' : '—'}</p></div>
          </div>
        </div>

        {/* Prix */}
        <div className="realease-card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Prix et negociation</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="realease-label">Prix affiche</label><input type="number" value={form.asking_price || ''} onChange={e => set('asking_price', +e.target.value || 0)} className="realease-input" data-testid="asking-price" /></div>
            <div><label className="realease-label">Prix cible</label><input type="number" value={form.target_price || ''} onChange={e => set('target_price', +e.target.value || 0)} className="realease-input" data-testid="target-price" /></div>
            <div className="col-span-2"><label className="realease-label">Negociation visee</label><p className="realease-input bg-gray-50 text-gray-600">{(live.negotiationPct || 0).toFixed(2)} %</p></div>
          </div>
        </div>
      </div>

      {/* Ligne 2 : Frais d'acquisition + Travaux PLEINE LARGEUR */}
      <div className="realease-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900">Frais d'acquisition &amp; enveloppe travaux</h3>
          <div className="text-xs text-gray-500">Tout est centralise ici : notaire, agence, travaux, ENEDIS/eau, mobilier et honoraires.</div>
        </div>

        {/* Notaire + Agence : 2 colonnes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div>
            <label className="realease-label">Frais de notaire (auto si vide)</label>
            <input type="number" value={form.notaire_fees ?? ''} onChange={e => set('notaire_fees', e.target.value === '' ? null : +e.target.value)} placeholder={fmt(live.notaireFeesAuto)} className="realease-input" />
            <p className="text-xs text-gray-400 mt-1">Auto : {fmt(live.notaireFeesAuto)} EUR (7,5 %)</p>
          </div>
          <div>
            <label className="realease-label">Frais agence (vendeur)</label>
            <input type="number" value={form.agency_fees || ''} onChange={e => set('agency_fees', +e.target.value || 0)} className="realease-input" />
          </div>
        </div>

        {/* Enveloppe Travaux : pleine largeur, 2 colonnes internes */}
        <div className="mt-6 pt-5 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Enveloppe travaux</h4>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-gray-500">Total saisi :</span>
              <span className="px-2.5 py-1 bg-[#008A8D]/10 text-[#008A8D] font-bold rounded-full">{fmt(live.worksItemsTotal || live.worksBudget)} EUR</span>
            </div>
          </div>

          {/* Calculateur au m2 - pleine largeur 4 colonnes */}
          <div className="mb-4 p-4 bg-gradient-to-r from-teal-50 to-white rounded-lg border border-teal-100" data-testid="works-calc-m2">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              <p className="text-sm font-semibold text-[#008A8D]">Calcul rapide au m&sup2;</p>
              <span className="text-xs text-gray-400 ml-auto">Choisissez un type de renovation et appliquez</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              <div>
                <label className="realease-label">Surface (m&sup2;)</label>
                <input type="number" value={form.works_surface ?? form.surface ?? ''}
                       onChange={e => set('works_surface', +e.target.value || 0)}
                       className="realease-input" placeholder="m2" data-testid="works-surface" />
              </div>
              <div>
                <label className="realease-label">Type de renovation</label>
                <select value={form.works_reno_type || ''}
                        onChange={e => set('works_reno_type', e.target.value)}
                        className="realease-input" data-testid="works-reno-type">
                  <option value="">Choisir...</option>
                  <option value="legere">Legere &mdash; 700 EUR/m&sup2;</option>
                  <option value="moyenne">Moyenne &mdash; 1 000 EUR/m&sup2;</option>
                  <option value="lourde">Lourde &mdash; 1 300 EUR/m&sup2;</option>
                </select>
              </div>
              <div>
                <label className="realease-label">Prix m&sup2; (EUR)</label>
                <input type="number"
                       value={form.works_price_per_sqm ?? ({ legere: 700, moyenne: 1000, lourde: 1300 }[form.works_reno_type] || '')}
                       onChange={e => set('works_price_per_sqm', +e.target.value || 0)}
                       className="realease-input" />
              </div>
              <div>
                <button onClick={() => {
                  const sqm = +form.works_surface || +form.surface || 0;
                  const price = +form.works_price_per_sqm || ({ legere: 700, moyenne: 1000, lourde: 1300 }[form.works_reno_type] || 0);
                  const total = Math.round(sqm * price);
                  if (!sqm || !price) { alert("Renseignez la surface et le type/prix m2."); return; }
                  const arr = Array.isArray(form.works_items) ? [...form.works_items] : [];
                  const filtered = arr.filter(x => !(x.label || '').startsWith('Forfait m2'));
                  filtered.unshift({ label: `Forfait m2 (${sqm} m2 x ${price} EUR) - ${form.works_reno_type || 'personnalise'}`, amount: total });
                  set('works_items', filtered);
                }} className="w-full px-3 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded hover:bg-[#006B6D] transition-colors" data-testid="apply-m2-calc">
                  Appliquer
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2 italic">Le forfait m² remplace toute ligne "Forfait m2" deja presente. Vous pouvez le combiner avec des postes detailles ci-dessous.</p>
          </div>

          {/* Liste de travaux detailles avec prix marche France - 3 colonnes */}
          <div className="mb-4">
            <details className="bg-amber-50 border border-amber-200 rounded-lg" data-testid="works-presets-details">
              <summary className="p-3 cursor-pointer text-sm font-semibold text-amber-900 flex items-center gap-2 list-none hover:bg-amber-100 rounded-lg">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Prix de reference France 2026 &mdash; cliquez pour ajouter (18 postes)
                <svg className="w-4 h-4 ml-auto transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </summary>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 text-xs">
                {[
                  { label: 'Demolition / depose cloison', price: 40, unit: 'm2' },
                  { label: 'Dalle beton 10 cm', price: 90, unit: 'm2' },
                  { label: 'Placo + isolation 120mm', price: 55, unit: 'm2' },
                  { label: 'Isolation combles 300mm', price: 35, unit: 'm2' },
                  { label: 'Electricite complete (mise aux normes)', price: 110, unit: 'm2' },
                  { label: 'Plomberie complete', price: 90, unit: 'm2' },
                  { label: 'Chauffage PAC air-eau installe', price: 8000, unit: 'forfait' },
                  { label: 'Radiateurs elec x5 (pose incluse)', price: 2500, unit: 'forfait' },
                  { label: 'Peinture murs + plafonds', price: 28, unit: 'm2' },
                  { label: 'Pose carrelage + colle', price: 75, unit: 'm2' },
                  { label: 'Pose parquet stratifie', price: 45, unit: 'm2' },
                  { label: 'Cuisine equipee standard', price: 8000, unit: 'forfait' },
                  { label: 'Salle de bains complete', price: 9000, unit: 'forfait' },
                  { label: 'WC suspendu + faience', price: 2500, unit: 'forfait' },
                  { label: 'Menuiseries PVC DV (par fenetre)', price: 900, unit: 'unite' },
                  { label: 'Porte palier blindee', price: 1800, unit: 'unite' },
                  { label: 'Volets roulants elec (par fenetre)', price: 750, unit: 'unite' },
                  { label: 'VMC double flux', price: 3500, unit: 'forfait' },
                ].map((p, idx) => (
                  <button key={idx}
                          onClick={() => {
                            const sqm = +form.works_surface || +form.surface || 0;
                            const qty = prompt(`${p.label}\nPrix unitaire : ${p.price} EUR / ${p.unit}\n\nQuantite${p.unit === 'm2' && sqm ? ` (surface projet = ${sqm} m2)` : ''} :`, p.unit === 'm2' && sqm ? String(sqm) : '1');
                            if (!qty) return;
                            const q = parseFloat(qty);
                            if (isNaN(q) || q <= 0) return;
                            const total = Math.round(q * p.price);
                            const arr = Array.isArray(form.works_items) ? [...form.works_items] : [];
                            arr.push({ label: `${p.label} (${q} ${p.unit} x ${p.price} EUR)`, amount: total });
                            set('works_items', arr);
                          }}
                          className="text-left px-2 py-1.5 rounded bg-white border border-amber-200 hover:bg-amber-100 hover:border-amber-300 text-amber-900 transition-colors"
                          title={`${p.price} EUR / ${p.unit}`}
                          data-testid={`preset-work-${idx}`}>
                    <div className="font-semibold truncate">{p.label}</div>
                    <div className="text-[10px] text-amber-700">{p.price} EUR / {p.unit}</div>
                  </button>
                ))}
              </div>
            </details>
          </div>

          {/* Postes saisis - tableau avec entete clair */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="grid grid-cols-12 gap-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
              <div className="col-span-8">Poste de travaux</div>
              <div className="col-span-3 text-right">Montant (EUR)</div>
              <div className="col-span-1"></div>
            </div>
            {(form.works_items || []).length === 0 && (
              <p className="text-sm text-gray-400 italic py-3 text-center">Aucune ligne &mdash; utilisez le calcul m&sup2;, les prix de reference, ou "+ Ajouter une ligne".</p>
            )}
            {(form.works_items || []).map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 mb-2 items-center" data-testid={`works-item-${i}`}>
                <div className="col-span-8">
                  <input type="text" value={it.label || ''}
                         onChange={e => {
                           const arr = [...(form.works_items || [])];
                           arr[i] = { ...arr[i], label: e.target.value };
                           set('works_items', arr);
                         }}
                         placeholder="Ex: Electricite, Plomberie, Cuisine..."
                         className="realease-input text-sm bg-white"
                         data-testid={`works-label-${i}`} />
                </div>
                <div className="col-span-3">
                  <input type="number" value={it.amount ?? ''}
                         onChange={e => {
                           const arr = [...(form.works_items || [])];
                           arr[i] = { ...arr[i], amount: +e.target.value || 0 };
                           set('works_items', arr);
                         }}
                         placeholder="0"
                         className="realease-input text-sm text-right bg-white"
                         data-testid={`works-amount-${i}`} />
                </div>
                <button onClick={() => set('works_items', (form.works_items || []).filter((_, idx) => idx !== i))}
                        className="col-span-1 text-red-500 text-lg hover:bg-red-50 rounded py-1 transition-colors"
                        title="Supprimer cette ligne"
                        data-testid={`remove-works-${i}`}>×</button>
              </div>
            ))}
            <button onClick={() => set('works_items', [...(form.works_items || []), { label: '', amount: 0 }])}
                    className="mt-2 text-sm text-[#008A8D] hover:underline font-semibold"
                    data-testid="add-works-item">+ Ajouter une ligne libre</button>
          </div>

          {/* ENEDIS / Eau / Sous-total : 3 colonnes claires */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label className="realease-label">Raccordement ENEDIS</label>
              <input type="number" value={form.enedis_fees ?? form.enedis_water_fees ?? ''} onChange={e => set('enedis_fees', +e.target.value || 0)} className="realease-input bg-white" data-testid="enedis-fees" placeholder="0" />
            </div>
            <div>
              <label className="realease-label">Raccordement eau</label>
              <input type="number" value={form.water_fees ?? ''} onChange={e => set('water_fees', +e.target.value || 0)} className="realease-input bg-white" data-testid="water-fees" placeholder="0" />
            </div>
            <div>
              <p className="realease-label">Sous-total enveloppe</p>
              <p className="realease-input bg-white font-bold text-[#008A8D] text-base">{fmt((live.worksBudget || 0) + (live.enedis || 0))} EUR</p>
            </div>
          </div>

          {/* Toggle honoraires + recap final */}
          <div className="mt-4 flex items-center justify-between p-4 bg-[#008A8D]/5 rounded-lg border border-[#008A8D]/20 flex-wrap gap-3">
            <div className="text-sm flex-1 min-w-[260px]">
              <p className="font-semibold text-gray-800">Integrer les honoraires RealEase dans l'enveloppe travaux</p>
              <p className="text-xs text-gray-500 mt-0.5">Active : l'enveloppe travaux presentee a la banque inclut les honoraires RealEase.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="toggle-include-fees">
              <input type="checkbox"
                     checked={!!form.include_realease_fees_in_works}
                     onChange={e => set('include_realease_fees_in_works', e.target.checked)}
                     className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-[#008A8D] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          </div>

          <div className="text-right text-sm mt-3 text-gray-600">
            Enveloppe banque (travaux + ENEDIS/eau + mobilier{form.include_realease_fees_in_works ? ' + honoraires' : ''}) :
            {' '}<b className="text-[#008A8D] text-base" data-testid="works-envelope-bank">{fmt(live.worksEnvelopeBank)} EUR</b>
          </div>
        </div>

        {/* Mobilier + Honoraires : 2 colonnes en bas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-5 border-t border-gray-200">
          <div>
            <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Mobilier</h4>
            <input type="number" value={form.furniture_budget || ''} onChange={e => set('furniture_budget', +e.target.value || 0)} className="realease-input" placeholder="Mobilier / amenagement (EUR)" data-testid="furniture-budget" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Honoraires RealEase</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="realease-label">Assiette</label>
                <p className="realease-input bg-gray-50 text-gray-600 text-sm">{fmt(live.realeaseBase)} EUR</p>
              </div>
              <div>
                <label className="realease-label">Honoraires (EUR){live.realeaseFeeOverridden && <span className="text-amber-600 font-bold"> — manuel</span>}</label>
                <input type="number"
                  value={live.realeaseFeeOverridden ? form.realease_fee_override : ''}
                  onChange={e => set('realease_fee_override', e.target.value === '' ? -1 : (+e.target.value || 0))}
                  placeholder={`${fmt(live.realeaseFeeAuto.amount)} (auto)`}
                  className={`realease-input font-semibold text-sm ${live.realeaseFeeOverridden ? 'border-amber-400 bg-amber-50' : 'bg-[#E8DCC4]/30'}`}
                  data-testid="realease-fee-override-input" />
                <p className="text-[11px] text-gray-500 mt-1">
                  {live.realeaseFeeOverridden
                    ? <>Appliqué : <b>{fmt(live.realeaseFee.amount)} EUR</b> ({live.realeaseFee.rate} %) — barème auto : {fmt(live.realeaseFeeAuto.amount)} EUR ({live.realeaseFeeAuto.rate} %)
                        <button type="button" onClick={() => set('realease_fee_override', -1)} className="ml-1.5 text-[#008A8D] underline" data-testid="realease-fee-reset-btn">rétablir l'auto</button></>
                    : <>Barème auto : <b>{fmt(live.realeaseFee.amount)} EUR</b> ({live.realeaseFee.rate} %). Saisissez un montant pour le remplacer, videz le champ pour revenir à l'auto.</>}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Assiette = prix cible + travaux + ENEDIS/eau + mobilier (sans notaire/agence)</p>
          </div>
        </div>
      </div>

      {/* Ligne 3 : Financement + Revenus + Charges (2 colonnes) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financement */}
        <div className="realease-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Financement</h3>
            <MarketRatesBtn duration={form.financing?.loan_duration_years || 25} onPick={(rate) => setFin('interest_rate', rate)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="realease-label">Apport</label><input type="number" value={form.financing?.down_payment || ''} onChange={e => setFin('down_payment', +e.target.value || 0)} className="realease-input" data-testid="down-payment" /></div>
            <div><label className="realease-label">Capital emprunte</label><p className="realease-input bg-gray-50 text-gray-600">{fmt(live.loanAmount)} EUR</p></div>
            <div><label className="realease-label">Duree (annees)</label>
              <select value={form.financing?.loan_duration_years || 25} onChange={e => setFin('loan_duration_years', +e.target.value)} className="realease-input">
                <option value={15}>15 ans</option><option value={20}>20 ans</option><option value={25}>25 ans</option>
              </select>
            </div>
            <div><label className="realease-label">Taux interet (%)</label><input type="number" step="0.01" value={form.financing?.interest_rate ?? ''} onChange={e => setFin('interest_rate', +e.target.value || 0)} className="realease-input" data-testid="interest-rate" /></div>
            <div><label className="realease-label">Taux assurance (%)</label><input type="number" step="0.01" value={form.financing?.insurance_rate ?? ''} onChange={e => setFin('insurance_rate', +e.target.value || 0)} className="realease-input" data-testid="insurance-rate" /></div>
            <div><label className="realease-label">Mensualite (capital + assurance)</label><p className="realease-input bg-gray-50 text-gray-600 font-semibold">{fmt(live.monthly.total)} EUR</p></div>
          </div>

          {/* Revente de terrain */}
          <div className={`mt-4 rounded-lg border p-3 ${form.land_sale_enabled ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200'}`} data-testid="land-sale-block">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 cursor-pointer">
              <input type="checkbox" checked={!!form.land_sale_enabled}
                     onChange={e => set('land_sale_enabled', e.target.checked)}
                     className="accent-[#008A8D] w-4 h-4" data-testid="land-sale-enable" />
              Revente d'un terrain (détaché de l'opération)
            </label>
            {form.land_sale_enabled && (
              <div className="mt-3 space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="realease-label">Prix de vente du terrain</label>
                    <input type="number" value={form.land_sale_price || ''} onChange={e => set('land_sale_price', +e.target.value || 0)}
                           className="realease-input" data-testid="land-sale-price" /></div>
                  <div><label className="realease-label">Opération nette après revente</label>
                    <p className="realease-input bg-gray-50 text-gray-600 font-semibold">{fmt(live.netOperationAfterLandSale)} EUR</p></div>
                </div>
                <div>
                  <label className="realease-label">Utilisation du produit de la vente</label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <label className={`flex-1 flex items-center gap-2 text-xs rounded-lg border px-3 py-2 cursor-pointer ${(form.land_sale_use || 'tresorerie') === 'remboursement' ? 'border-[#008A8D] bg-[#008A8D]/5 font-semibold text-[#008A8D]' : 'border-gray-200 text-gray-600'}`}>
                      <input type="radio" name="land-sale-use" checked={(form.land_sale_use || 'tresorerie') === 'remboursement'}
                             onChange={() => set('land_sale_use', 'remboursement')} className="accent-[#008A8D]" data-testid="land-sale-use-remboursement" />
                      Remboursement anticipé du crédit
                    </label>
                    <label className={`flex-1 flex items-center gap-2 text-xs rounded-lg border px-3 py-2 cursor-pointer ${(form.land_sale_use || 'tresorerie') === 'tresorerie' ? 'border-[#008A8D] bg-[#008A8D]/5 font-semibold text-[#008A8D]' : 'border-gray-200 text-gray-600'}`}>
                      <input type="radio" name="land-sale-use" checked={(form.land_sale_use || 'tresorerie') === 'tresorerie'}
                             onChange={() => set('land_sale_use', 'tresorerie')} className="accent-[#008A8D]" data-testid="land-sale-use-tresorerie" />
                      Conservé en trésorerie
                    </label>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500" data-testid="land-sale-note">
                  Les honoraires RealEase restent calculés sur l'opération complète (non impactés). Les rendements sont calculés sur le coût net après revente.
                  {(form.land_sale_use || 'tresorerie') === 'remboursement'
                    ? ' Le produit rembourse le crédit par anticipation : capital emprunté et mensualité réduits, cashflow amélioré.'
                    : ' Le produit est conservé en trésorerie : capital emprunté et mensualité inchangés.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Revenus */}
        <div className="realease-card p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Revenus locatifs</h3>
          <RentScenarioBar form={form} setForm={setForm} live={live} />
          {/* Multi-lots / appartements */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Lots / appartements (loyer detaille)</label>
              <button onClick={() => set('rental_units', [...(form.rental_units || []), { label: `Lot ${(form.rental_units || []).length + 1}`, monthly_rent: 0, current_monthly_rent: 0 }])}
                       className="text-xs text-[#008A8D] font-semibold hover:underline" data-testid="add-rental-unit">+ Ajouter un lot</button>
            </div>
            {(form.rental_units || []).length === 0 ? (
              <p className="text-xs text-gray-400 italic mb-2">Aucun lot detaille &mdash; saisissez le loyer total ci-dessous, ou ajoutez vos lots un par un (T1, T2, studio, garage...).</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 items-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-1">
                  <div className="col-span-5">Lot</div>
                  <div className="col-span-3 text-right">Loyer actuel</div>
                  <div className="col-span-3 text-right">Loyer projeté</div>
                  <div className="col-span-1"></div>
                </div>
                {(form.rental_units || []).map((u, i) => {
                  const stRent = unitMonthlyRent(u);
                  return (
                  <div key={i} data-testid={`rental-unit-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                    <input type="text" value={u.label || ''}
                            onChange={e => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], label: e.target.value }; set('rental_units', arr); }}
                            placeholder="Ex: T1 RDC, Studio 1er, Garage..."
                            className="col-span-5 realease-input text-sm" />
                    <input type="number" value={u.current_monthly_rent ?? ''}
                            onChange={e => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], current_monthly_rent: +e.target.value || 0 }; set('rental_units', arr); }}
                            placeholder="Actuel"
                            className="col-span-3 realease-input text-sm text-right"
                            data-testid={`rental-unit-current-${i}`} />
                    <input type="number" value={u.short_term ? stRent : (u.monthly_rent ?? '')}
                            disabled={!!u.short_term}
                            onChange={e => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], monthly_rent: +e.target.value || 0 }; set('rental_units', arr); }}
                            placeholder="Projeté"
                            className={`col-span-3 realease-input text-sm text-right ${u.short_term ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            data-testid={`rental-unit-projected-${i}`} />
                    <button onClick={() => set('rental_units', (form.rental_units || []).filter((_, j) => j !== i))}
                             className="col-span-1 text-red-500 text-lg hover:bg-red-50 rounded">&times;</button>
                    </div>
                    <div className="pl-1 mt-1 mb-2">
                      <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={!!u.short_term}
                               onChange={e => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], short_term: e.target.checked, monthly_rent: e.target.checked ? unitMonthlyRent({ ...arr[i], short_term: true }) : (arr[i].monthly_rent || 0) }; set('rental_units', arr); }}
                               className="accent-[#008A8D]"
                               data-testid={`rental-unit-shortterm-${i}`} />
                        Courte durée (Airbnb, location atypique)
                      </label>
                      {u.short_term && (() => {
                        const upd = (patch) => { const arr = [...(form.rental_units || [])]; const next = { ...arr[i], ...patch }; next.monthly_rent = unitMonthlyRent(next); arr[i] = next; set('rental_units', arr); };
                        return (
                        <div className="mt-1.5 bg-[#008A8D]/5 border border-[#008A8D]/15 rounded-lg p-2.5" data-testid={`rental-unit-shortterm-panel-${i}`}>
                          <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-4">
                            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{u.seasonal ? 'Nuitée basse saison (EUR)' : 'Prix nuitée (EUR)'}</label>
                            <input type="number" value={u.nightly_rate ?? ''}
                                   onChange={e => upd({ nightly_rate: +e.target.value || 0 })}
                                   placeholder="Ex: 80"
                                   className="realease-input text-sm text-right mt-0.5"
                                   data-testid={`rental-unit-nightly-${i}`} />
                          </div>
                          <div className="col-span-4">
                            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{u.seasonal ? 'Remplissage basse saison (%)' : 'Taux remplissage (%)'}</label>
                            <input type="number" min="0" max="100" value={u.occupancy_rate ?? ''}
                                   onChange={e => upd({ occupancy_rate: Math.min(100, Math.max(0, +e.target.value || 0)) })}
                                   placeholder="Ex: 65"
                                   className="realease-input text-sm text-right mt-0.5"
                                   data-testid={`rental-unit-occupancy-${i}`} />
                          </div>
                          <div className="col-span-4 text-right pb-1">
                            <p className="text-xs text-gray-700">≈ <b className="text-[#008A8D]" data-testid={`rental-unit-shortterm-rent-${i}`}>{fmt(stRent)}</b> / mois</p>
                            <p className="text-[9px] text-gray-500">{u.seasonal ? 'moyenne annuelle haute + basse saison' : 'nuitée × 30,4 nuits × remplissage'}</p>
                          </div>
                          </div>
                          {u.seasonal && (
                            <div className="grid grid-cols-12 gap-2 items-end mt-2 pt-2 border-t border-[#008A8D]/15">
                              <div className="col-span-4">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Nuitée haute saison (EUR)</label>
                                <input type="number" value={u.hs_nightly_rate ?? ''}
                                       onChange={e => upd({ hs_nightly_rate: +e.target.value || 0 })}
                                       placeholder="Ex: 120"
                                       className="realease-input text-sm text-right mt-0.5"
                                       data-testid={`rental-unit-hs-nightly-${i}`} />
                              </div>
                              <div className="col-span-4">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Remplissage haute saison (%)</label>
                                <input type="number" min="0" max="100" value={u.hs_occupancy_rate ?? ''}
                                       onChange={e => upd({ hs_occupancy_rate: Math.min(100, Math.max(0, +e.target.value || 0)) })}
                                       placeholder="Ex: 85"
                                       className="realease-input text-sm text-right mt-0.5"
                                       data-testid={`rental-unit-hs-occupancy-${i}`} />
                              </div>
                              <div className="col-span-4">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Mois en haute saison</label>
                                <input type="number" min="0" max="12" value={u.hs_months ?? ''}
                                       onChange={e => upd({ hs_months: Math.min(12, Math.max(0, +e.target.value || 0)) })}
                                       placeholder="Ex: 4"
                                       className="realease-input text-sm text-right mt-0.5"
                                       data-testid={`rental-unit-hs-months-${i}`} />
                              </div>
                            </div>
                          )}
                          <label className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
                            <input type="checkbox" checked={!!u.seasonal}
                                   onChange={e => upd(e.target.checked
                                     ? { seasonal: true, hs_months: u.hs_months || 4, hs_nightly_rate: u.hs_nightly_rate || u.nightly_rate || 0, hs_occupancy_rate: u.hs_occupancy_rate || u.occupancy_rate || 0 }
                                     : { seasonal: false })}
                                   className="accent-[#008A8D]"
                                   data-testid={`rental-unit-seasonal-${i}`} />
                            Saisonnalité (nuitée / remplissage différents en haute et basse saison)
                          </label>
                          <div className="mt-2 pt-2 border-t border-[#008A8D]/15 grid grid-cols-12 gap-2 items-center">
                            <label className="col-span-7 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Loyer équivalent en location classique (pour comparatif)</label>
                            <input type="number" value={u.lt_monthly_rent ?? ''}
                                   onChange={e => upd({ lt_monthly_rent: +e.target.value || 0 })}
                                   placeholder="Ex: 650 EUR / mois"
                                   className="col-span-5 realease-input text-sm text-right"
                                   data-testid={`rental-unit-lt-rent-${i}`} />
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  </div>
                  );
                })}
                <div className="flex justify-end gap-6 text-xs text-gray-600 mt-1">
                  {live.unitsCurrentRentTotal > 0 && (
                    <span>Total actuel : <b className="text-gray-700">{fmt(live.unitsCurrentRentTotal || 0)} EUR / mois</b></span>
                  )}
                  <span>Total projeté : <b className="text-[#008A8D]">{fmt(live.unitsRentTotal || 0)} EUR / mois</b></span>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="realease-label">Loyer mensuel actuel</label>
              <input type="number" value={live.unitsCurrentRentTotal > 0 ? live.unitsCurrentRentTotal : (form.current_monthly_rent || '')} disabled={live.unitsCurrentRentTotal > 0} onChange={e => set('current_monthly_rent', +e.target.value || 0)} className={`realease-input ${live.unitsCurrentRentTotal > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`} data-testid="current-monthly-rent" placeholder="0 si bien vacant" />
              <p className="text-[10px] text-gray-500 mt-1">Loyers en place aujourd'hui (avant travaux). Laissez 0 si bien vacant.</p>
            </div>
            <div>
              <label className="realease-label">Loyer mensuel projeté</label>
              <input type="number" value={live.unitsRentTotal > 0 ? live.unitsRentTotal : (form.monthly_rent || '')} disabled={live.unitsRentTotal > 0} onChange={e => set('monthly_rent', +e.target.value || 0)} className={`realease-input ${live.unitsRentTotal > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`} data-testid="monthly-rent" />
              <p className="text-[10px] text-gray-500 mt-1">Loyers cibles après mise en location / optimisation.</p>
            </div>
            <div><label className="realease-label">Autres revenus / mois</label><input type="number" value={form.other_income || ''} onChange={e => set('other_income', +e.target.value || 0)} className="realease-input" /></div>
            <div><label className="realease-label">Total revenus annuels (projeté)</label><p className="realease-input bg-gray-50 text-gray-600">{fmt(live.annualRent)} EUR</p></div>
          </div>
          {live.hasCurrentRent && (
            <div className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50/50">
              <p className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide mb-2">Rentabilité comparée — Actuel vs Projeté</p>
              <div className="grid grid-cols-2 gap-3 text-xs" data-testid="current-vs-projected-kpis">
                <div className="bg-white p-2 rounded border border-gray-200">
                  <p className="text-gray-500 text-[10px] uppercase">Situation actuelle</p>
                  <p className="text-gray-900 font-semibold mt-1">Rendement net : <span className={live.currentNetYield >= 0 ? 'text-gray-800' : 'text-red-600'}>{live.currentNetYield.toFixed(2)} %</span></p>
                  <p className="text-gray-700">Cashflow / mois : <span className={live.currentMonthlyCashflow >= 0 ? 'text-emerald-700' : 'text-red-600'}>{fmt(Math.round(live.currentMonthlyCashflow))} EUR</span></p>
                  <p className="text-[10px] text-gray-500 mt-1">Loyers en place : {fmt(live.effectiveCurrentMonthlyRent)} EUR / mois</p>
                </div>
                <div className="bg-white p-2 rounded border border-[#008A8D]/30">
                  <p className="text-[#008A8D] text-[10px] uppercase font-bold">Après optimisation</p>
                  <p className="text-gray-900 font-semibold mt-1">Rendement net : <span className="text-[#008A8D]">{live.netYield.toFixed(2)} %</span></p>
                  <p className="text-gray-700">Cashflow / mois : <span className={live.monthlyCashflow >= 0 ? 'text-emerald-700' : 'text-red-600'}>{fmt(Math.round(live.monthlyCashflow))} EUR</span></p>
                  <p className="text-[10px] text-gray-500 mt-1">Loyers cibles : {fmt(live.unitsRentTotal > 0 ? live.unitsRentTotal : (+form.monthly_rent || 0))} EUR / mois</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comparatif location classique vs courte duree */}
        {(form.rental_units || []).some(u => u && u.short_term) && (
          <LongShortComparison form={form} live={live} />
        )}

        {/* Charges */}
        <div className="realease-card p-6 lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-4">Charges annuelles</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="realease-label">Taxe fonciere</label><input type="number" value={form.annual_taxes || ''} onChange={e => set('annual_taxes', +e.target.value || 0)} className="realease-input" /></div>
            <div><label className="realease-label">Assurance PNO</label><input type="number" value={form.annual_insurance || ''} onChange={e => set('annual_insurance', +e.target.value || 0)} className="realease-input" /></div>
            <div><label className="realease-label">Comptabilite</label><input type="number" value={form.annual_accounting || ''} onChange={e => set('annual_accounting', +e.target.value || 0)} className="realease-input" /></div>
            {(+form.annual_other_charges || 0) > 0 && (
              <div><label className="realease-label">Autres charges (ancien champ global)</label><input type="number" value={form.annual_other_charges || ''} onChange={e => set('annual_other_charges', +e.target.value || 0)} className="realease-input" data-testid="analysis-legacy-other-charges" /></div>
            )}
            <div className="col-span-2 md:col-span-4"><label className="realease-label">Total charges (incl. gestion locative si activee)</label><p className="realease-input bg-gray-50 text-gray-600">{fmt(live.annualCharges)} EUR</p></div>
          </div>
          {/* Charges nommees (liste editable label + montant) */}
          <div className="mt-4" data-testid="analysis-custom-charges">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Autres charges (détaillées)</p>
              <button type="button"
                onClick={() => set('custom_annual_charges', [...(form.custom_annual_charges || []), { label: '', amount: 0 }])}
                className="text-xs text-[#008A8D] font-semibold hover:underline" data-testid="analysis-add-custom-charge">
                + Ajouter une charge
              </button>
            </div>
            {(form.custom_annual_charges || []).length === 0 ? (
              <p className="text-[11px] text-gray-400 italic">Aucune charge détaillée. Cliquez sur «Ajouter une charge» pour saisir des charges nommées (copropriété, syndic, abonnement chaudière, etc.).</p>
            ) : (
              <div className="space-y-1.5">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-gray-400 font-semibold">
                  <span className="col-span-5">Nom de la charge</span>
                  <span className="col-span-3 text-right">EUR / mois</span>
                  <span className="col-span-3 text-right">EUR / an</span>
                  <span className="col-span-1"></span>
                </div>
                {(form.custom_annual_charges || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`analysis-custom-charge-${i}`}>
                    <input type="text" value={c.label || ''}
                      onChange={e => { const arr = [...(form.custom_annual_charges || [])]; arr[i] = { ...arr[i], label: e.target.value }; set('custom_annual_charges', arr); }}
                      placeholder="Ex : Copropriété, syndic, abonnement chaudière..."
                      className="col-span-5 realease-input text-sm" data-testid={`analysis-custom-charge-label-${i}`} />
                    <input type="number" value={c.amount ? Math.round(c.amount / 12 * 100) / 100 : ''}
                      onChange={e => { const m = +e.target.value || 0; const arr = [...(form.custom_annual_charges || [])]; arr[i] = { ...arr[i], amount: Math.round(m * 12 * 100) / 100 }; set('custom_annual_charges', arr); }}
                      placeholder="/ mois"
                      className="col-span-3 realease-input text-sm text-right" data-testid={`analysis-custom-charge-monthly-${i}`} />
                    <input type="number" value={c.amount ?? ''}
                      onChange={e => { const arr = [...(form.custom_annual_charges || [])]; arr[i] = { ...arr[i], amount: +e.target.value || 0 }; set('custom_annual_charges', arr); }}
                      placeholder="/ an"
                      className="col-span-3 realease-input text-sm text-right" data-testid={`analysis-custom-charge-amount-${i}`} />
                    <button type="button"
                      onClick={() => set('custom_annual_charges', (form.custom_annual_charges || []).filter((_, j) => j !== i))}
                      className="col-span-1 text-red-500 hover:bg-red-50 rounded text-lg leading-none" title="Supprimer cette charge"
                      data-testid={`analysis-custom-charge-remove-${i}`}>×</button>
                  </div>
                ))}
                <div className="text-right text-xs text-gray-500 mt-1">
                  Sous-total charges détaillées : <span className="font-semibold text-gray-700">{fmt(live.customAnnualChargesTotal)} EUR / an ({fmt(Math.round(live.customAnnualChargesTotal / 12))} EUR / mois)</span>
                </div>
              </div>
            )}
          </div>
          {/* Toggle gestion locative */}
          <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-white rounded-lg border border-amber-200" data-testid="property-management-block">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.enable_property_management}
                      onChange={e => set('enable_property_management', e.target.checked)}
                      className="w-5 h-5 rounded text-[#008A8D] mt-0.5"
                      data-testid="toggle-property-management" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800">Inclure une gestion locative ({form.property_management_rate ?? 7} % des loyers HC)</p>
                <p className="text-xs text-gray-500 mt-0.5">Cocher si une agence prend en charge la gestion locative. Calcul auto = {form.property_management_rate ?? 7} % du loyer annuel, ajoute aux charges.</p>
              </div>
              {form.enable_property_management && (
                <div className="flex items-center gap-2">
                  <input type="number" step="0.5" value={form.property_management_rate ?? 7}
                          onChange={e => set('property_management_rate', +e.target.value || 0)}
                          className="realease-input w-20 text-sm" />
                  <span className="text-sm text-gray-700">%</span>
                </div>
              )}
            </label>
            {form.enable_property_management && (
              <p className="text-right text-sm text-gray-700 mt-2">
                Cout gestion locative annuel : <b className="text-[#008A8D]">{fmt(live.propertyMgmtAnnual || 0)} EUR / an</b>
                {(form.rental_units || []).some(u => u && u.short_term) && (
                  <span className="block text-[10px] text-amber-600 mt-0.5" data-testid="pm-lt-only-note">Calculé sur les loyers longue durée uniquement — les lots en courte durée sont exclus (gestion à ajouter manuellement dans les charges).</span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cashflow detail */}
      <div className="realease-card p-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900">Cashflow previsionnel</h3>
          <span className="text-xs text-gray-500">Calcul sur la duree choisie : <b className="text-[#008A8D]">{form.financing?.loan_duration_years || 25} ans</b></span>
        </div>
        <table className="w-full text-sm" data-testid="cashflow-table">
          <tbody className="divide-y">
            <tr><td className="py-2">+ Revenus locatifs annuels</td><td className="py-2 text-right">{fmt(live.annualRent)} EUR</td></tr>
            <tr><td className="py-2">- Charges d'exploitation</td><td className="py-2 text-right">{fmt(live.annualCharges)} EUR</td></tr>
            <tr><td className="py-2">- Mensualites pret (annualisees)</td><td className="py-2 text-right">{fmt(live.annualLoanPayment)} EUR</td></tr>
            <tr className="font-bold"><td className="py-2">= Cashflow annuel net</td><td className={`py-2 text-right ${live.annualCashflow >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="cashflow-annual">{fmt(live.annualCashflow)} EUR</td></tr>
            <tr className="font-bold"><td className="py-2">= Cashflow mensuel net</td><td className={`py-2 text-right ${live.monthlyCashflow >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="cashflow-monthly">{fmt(live.monthlyCashflow)} EUR</td></tr>
          </tbody>
        </table>
      </div>

      {/* Comparateur multi-scenarios desactive (retire a la demande utilisateur). */}
      {/* <ScenarioComparator form={form} live={live} /> */}

      {/* Images & Plans */}
      <ImagesManager analysisId={id} levels={form.levels || ["RDC", "R+1", "R+2", "R+3"]}
                     onLevelsChange={(lvls) => set('levels', lvls)} />

      {/* Travaux & Phases */}
      <WorksEditor form={form} set={set} />

      {/* Section ville (pitch + atouts) */}
      <CitySection form={form} set={set} analysisId={id} />

      {/* Calendrier projet */}
      <TimelineEditor form={form} set={set} />

      {/* Texte PDF custom */}
      <div className="realease-card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Personnalisation du PDF</h3>
        <div className="space-y-3">
          <div>
            <label className="realease-label">Introduction (laissez vide pour le texte par defaut)</label>
            <textarea value={form.pdf_intro || ''} onChange={e => set('pdf_intro', e.target.value)} className="realease-input h-24" data-testid="pdf-intro" placeholder="Texte d'introduction personnalise..." />
          </div>
          <div>
            <label className="realease-label">Conclusion</label>
            <textarea value={form.pdf_conclusion || ''} onChange={e => set('pdf_conclusion', e.target.value)} className="realease-input h-24" data-testid="pdf-conclusion" placeholder="Texte de conclusion personnalise..." />
          </div>
          <div>
            <label className="realease-label">Notes complementaires</label>
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} className="realease-input h-20" placeholder="Notes internes ou affichees en fin de PDF..." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 mt-2 border-t border-gray-100">
            <div>
              <label className="realease-label flex items-center gap-2">
                <span className="text-[#008A8D]">►</span> Lien Google Drive Video du bien
              </label>
              <input type="url" value={form.video_link || ''}
                     onChange={e => set('video_link', e.target.value)}
                     placeholder="https://drive.google.com/file/d/..."
                     className="realease-input"
                     data-testid="pdf-video-link" />
              <p className="text-xs text-gray-500 mt-1">S'affichera comme bouton sur la couverture du PDF.</p>
            </div>
            <div>
              <label className="realease-label flex items-center gap-2">
                <span className="text-[#A57F4F]">360°</span> Lien Visite virtuelle (Girafe 360 / Matterport)
              </label>
              <input type="url" value={form.virtual_tour_link || ''}
                     onChange={e => set('virtual_tour_link', e.target.value)}
                     placeholder="https://app.girafe360.com/..."
                     className="realease-input"
                     data-testid="pdf-tour-link" />
              <p className="text-xs text-gray-500 mt-1">Permet au client de visiter le bien depuis le PDF.</p>
            </div>
          </div>
        </div>
      </div>
      </div>{/* end main column */}

      {/* Sidebar recap (sticky) */}
      <aside className="lg:w-80 shrink-0">
        <div className="lg:sticky lg:top-6 space-y-4">
          <RecapPanel form={form} live={live} />
          <InternalNotesPanel entityType="analysis" entityId={id} />
        </div>
      </aside>
      </div>{/* end 2-cols flex */}

      {showPreview && (
        <PdfPreviewModal
          url={`${API}/analyses/${id}/pdf`}
          title={`Apercu - ${form?.name || 'Dossier de presentation'}`}
          downloadName={`RealEase_Analyse_${form?.name || id}.pdf`}
          entityType="analysis"
          entityId={id}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showSendModal && (
        <SendModal
          mode={sendMode}
          analysis={analysis}
          form={form}
          projects={projects}
          onClose={() => setShowSendModal(false)}
          onSent={() => { setShowSendModal(false); fetchAnalysis(); }}
          onSave={save}
        />
      )}

      {showBankModal && (
        <BankDossierModal
          analysisId={id}
          analysisName={form?.name}
          form={form}
          live={live}
          set={set}
          onClose={() => setShowBankModal(false)}
          onSave={save}
        />
      )}
    </div>
  );
};


// ==================== MODALE DOSSIER BANQUE ====================
const BankDossierModal = ({ analysisId, analysisName, form, live, set, onClose, onSave }) => {
  const suggestion = Math.round(((live.worksBudget || 0) + (live.realeaseFee?.amount || 0) + (live.furniture || 0)) * 100) / 100;
  const [works, setWorks] = useState(String(form.bank_works_budget || suggestion));
  const [downloading, setDownloading] = useState(null);
  const w = parseFloat(works) || 0;
  const fin = form.financing || {};
  const down = +fin.down_payment || 0;
  const totalOpBank = live.totalOperation - live.worksBudget - (live.realeaseFee?.amount || 0) - (live.furniture || 0) + w;
  const loan = Math.max(totalOpBank - down, 0);
  const dur = +fin.loan_duration_years || 25;
  const mp = calcMonthlyPayment(loan, +fin.interest_rate || 3.65, dur, +fin.insurance_rate || 0.36);
  const annualCF = live.annualRent - live.annualCharges - mp.total * 12;
  const grossY = totalOpBank ? (live.annualRent / totalOpBank * 100) : 0;
  const netY = totalOpBank ? ((live.annualRent - live.annualCharges) / totalOpBank * 100) : 0;
  const margin = w - suggestion;

  const download = async (kind) => {
    if (w <= 0) { alert('Renseignez le forfait travaux.'); return; }
    setDownloading(kind);
    try {
      set('bank_works_budget', w);
      await onSave(true);
      const r = await axios.get(`${API}/analyses/${analysisId}/bank-dossier-pdf?works=${w}&kind=${kind}&_=${Date.now()}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `RealEase_Dossier_Banque_${(analysisName || analysisId).replace(/\s+/g, '_')}${kind === 'synthesis' ? '_Synthese' : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message || 'Erreur inconnue';
      if (e.response?.data instanceof Blob) {
        try { const text = await e.response.data.text(); try { detail = JSON.parse(text).detail || text; } catch { detail = text; } } catch {}
      }
      alert('Erreur génération dossier banque :\n\n' + detail);
    } finally { setDownloading(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bank-dossier-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Dossier banque</h2>
        <p className="text-xs text-gray-500 mb-4">
          Dossier de présentation complet (étude de marché, rentabilité projetée, cashflow prévisionnel) recalculé avec un
          forfait travaux à votre main. Les honoraires RealEase et le budget mobilier n'apparaissent pas : ils sont absorbés dans le forfait.
        </p>
        <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 mb-3">
          <div className="flex justify-between"><span className="text-gray-500">Budget travaux actuel</span><b>{fmt(live.worksBudget)} EUR</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Honoraires RealEase ({live.realeaseFee?.rate} %)</span><b>{fmt(live.realeaseFee?.amount)} EUR</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Budget mobilier</span><b>{fmt(live.furniture)} EUR</b></div>
          <div className="flex justify-between border-t border-gray-200 pt-1"><span className="text-gray-700 font-semibold">Suggestion (travaux + honoraires + mobilier)</span><b className="text-[#008A8D]">{fmt(suggestion)} EUR</b></div>
        </div>
        <div className="mb-3">
          <label className="realease-label">Forfait travaux affiché à la banque (EUR)</label>
          <input type="number" value={works} onChange={e => setWorks(e.target.value)} className="realease-input font-semibold" data-testid="bank-works-input" />
          {Math.abs(margin) > 0.5 && (
            <p className={`text-[11px] mt-1 ${margin > 0 ? 'text-emerald-600' : 'text-orange-500'}`}>
              {margin > 0 ? `Marge de ${fmt(margin)} EUR au-dessus de travaux + honoraires + mobilier` : `Attention : ${fmt(-margin)} EUR en dessous de travaux + honoraires + mobilier`}
            </p>
          )}
        </div>
        <div className="bg-[#F0FAFA] border border-[#008A8D]/20 rounded-lg p-3 text-sm space-y-1 mb-4" data-testid="bank-preview">
          <p className="text-[10px] uppercase font-bold text-[#008A8D] mb-1">Chiffres recalculés (version banque)</p>
          <div className="flex justify-between"><span className="text-gray-500">Coût total opération</span><b>{fmt(Math.round(totalOpBank))} EUR</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Emprunt (apport {fmt(down)} EUR)</span><b>{fmt(Math.round(loan))} EUR</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Mensualité ({dur} ans, ass. incluse)</span><b>{fmt(Math.round(mp.total))} EUR</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Cashflow mensuel</span><b className={annualCF >= 0 ? 'text-emerald-700' : 'text-red-600'}>{fmt(Math.round(annualCF / 12))} EUR</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Rendement brut / net</span><b>{grossY.toFixed(2)} % / {netY.toFixed(2)} %</b></div>
        </div>
        <div className="flex flex-col gap-2">
          <button onClick={() => download('full')} disabled={!!downloading} className="realease-btn-primary w-full disabled:opacity-50" data-testid="bank-download-full-btn">
            {downloading === 'full' ? 'Génération du dossier...' : '📑 Télécharger le dossier complet (banque)'}
          </button>
          <button onClick={() => download('synthesis')} disabled={!!downloading} className="px-4 py-2 border border-[#13736D] text-[#13736D] rounded-lg hover:bg-[#13736D]/5 font-semibold text-sm disabled:opacity-50" data-testid="bank-download-synthesis-btn">
            {downloading === 'synthesis' ? 'Génération...' : 'Synthèse 1 page (banque)'}
          </button>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 mt-1">Fermer</button>
        </div>
      </div>
    </div>
  );
};


// ==================== MODALE D'ENVOI ====================
const SendModal = ({ mode, analysis, form, projects, onClose, onSent, onSave }) => {
  const [projectId, setProjectId] = useState(analysis?.project_id || '');
  const [folderName, setFolderName] = useState('Documents');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    setSending(true); setError('');
    try {
      // Sauvegarde avant envoi pour s'assurer du contenu a jour
      await onSave(true);
      if (mode === 'drive') {
        if (!projectId) { setError('Selectionnez un projet'); setSending(false); return; }
        await axios.post(`${API}/analyses/${analysis.analysis_id}/send-to-drive`, {
          project_id: projectId, folder_name: folderName,
        });
      } else if (mode === 'synthesis') {
        if (!email) { setError('Email destinataire requis'); setSending(false); return; }
        await axios.post(`${API}/analyses/${analysis.analysis_id}/send-synthesis-to-client`, {
          email,
          message: message || null,
        });
      } else {
        await axios.post(`${API}/analyses/${analysis.analysis_id}/send-to-client`, {
          project_id: projectId || null,
          email: email || null,
          message: message || null,
        });
      }
      onSent();
      alert(
        mode === 'drive' ? 'PDF envoye sur Drive avec succes !'
        : mode === 'synthesis' ? 'Synthese envoyee par email avec succes !'
        : 'PDF envoye au client avec succes !'
      );
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" data-testid="send-modal">
        <h3 className="text-xl font-bold mb-4">
          {mode === 'drive' ? 'Envoyer sur Google Drive'
            : mode === 'synthesis' ? 'Envoyer la synthese banquier'
            : 'Envoyer au client'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {mode === 'drive'
            ? 'Le PDF sera depose dans le dossier du projet selectionne.'
            : mode === 'synthesis'
            ? 'Synthese executive 1 page A4 (recto) destinee au banquier ou au courtier. Le PDF est envoye en piece jointe d\'un email professionnel.'
            : 'Le client recevra le PDF par email et pourra le consulter dans son portail. Validez avant l\'envoi.'}
        </p>

        {mode === 'drive' && (
          <>
            <label className="realease-label">Projet</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="realease-input mb-3">
              <option value="">— Selectionnez —</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
            <label className="realease-label">Sous-dossier</label>
            <select value={folderName} onChange={e => setFolderName(e.target.value)} className="realease-input mb-3">
              <option value="Documents">Documents</option>
              <option value="01_Admin">01 Admin</option>
              <option value="02_Banque">02 Banque</option>
              <option value="CLIENT">CLIENT (racine)</option>
            </select>
          </>
        )}

        {mode === 'client' && (
          <>
            <label className="realease-label">Projet associe (optionnel)</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="realease-input mb-3">
              <option value="">— Aucun —</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
            <label className="realease-label">Email destinataire (laissez vide pour utiliser celui du lead)</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="realease-input mb-3" placeholder="client@email.com" data-testid="send-client-email" />
            <label className="realease-label">Message personnalise (optionnel)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} className="realease-input h-24" placeholder="Bonjour, voici l'analyse..." />
          </>
        )}

        {mode === 'synthesis' && (
          <>
            <label className="realease-label">Email destinataire (banquier, courtier...)</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="realease-input mb-3" placeholder="banquier@banque.fr" data-testid="send-synthesis-email" required />
            <label className="realease-label">Message personnalise (optionnel)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} className="realease-input h-24" placeholder="Bonjour, veuillez trouver ci-joint la synthese executive 1 page de notre dossier d'investissement..." />
          </>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 my-4 text-sm">
          <p className="font-medium text-amber-800">Verifiez le PDF avant envoi</p>
          <p className="text-amber-700 mt-1">Vous pouvez modifier les textes d'intro / conclusion / notes dans la page detail. Les modifications sont prises en compte automatiquement.</p>
        </div>

        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg">Annuler</button>
          <button onClick={handleSend} disabled={sending} className="realease-btn-primary disabled:opacity-50" data-testid="confirm-send">
            {sending ? 'Envoi...' : (mode === 'drive' ? 'Envoyer sur Drive' : 'Envoyer au client')}
          </button>
        </div>
      </div>
    </div>
  );
};


// ==================== FINANCE DEADLINE MODAL ====================
export const FinanceDeadlineWidget = ({ projectId, project, onUpdated }) => {
  const [deadline, setDeadline] = useState('');
  const [reminders, setReminders] = useState([]);
  const [editing, setEditing] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/finance-deadlines/${projectId}`);
      setDeadline(r.data.finance_deadline);
      setReminders(r.data.finance_reminders || []);
      if (r.data.finance_deadline) {
        setNewDate(r.data.finance_deadline.slice(0, 10));
      }
    } catch (e) {}
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isCompromisOrAfter = ['notaire', 'administratif', 'travaux', 'livraison', 'termine'].includes(project?.status);
  const compromisSigned = project?.compromis_signed || isCompromisOrAfter;

  if (!compromisSigned && !deadline) return null;

  const save = async () => {
    if (!newDate) { alert('Date requise'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/finance-deadlines`, { project_id: projectId, deadline_date: newDate });
      await fetchData();
      setEditing(false);
      onUpdated && onUpdated();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="realease-card p-6 border-l-4 border-amber-500" data-testid="finance-deadline-widget">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">Offres de financement (notaire)</h3>
          <p className="text-xs text-gray-500">Date limite + relances automatiques J-30, J-15, J-7</p>
        </div>
        {!editing && <button onClick={() => setEditing(true)} className="text-sm text-[#008A8D] hover:underline" data-testid="edit-finance-deadline">{deadline ? 'Modifier' : 'Definir'}</button>}
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="realease-input flex-1" data-testid="finance-deadline-input" />
          <button onClick={save} disabled={saving} className="realease-btn-primary disabled:opacity-50" data-testid="save-finance-deadline">
            {saving ? '...' : 'Valider'}
          </button>
          <button onClick={() => setEditing(false)} className="px-3 py-2 border rounded-lg">Annuler</button>
        </div>
      ) : deadline ? (
        <>
          <p className="text-2xl font-bold text-amber-700">{new Date(deadline).toLocaleDateString('fr-FR')}</p>
          <div className="mt-3 space-y-1">
            {reminders.map(r => (
              <div key={r.key} className="flex items-center justify-between text-sm">
                <span>{r.label} ({new Date(r.trigger_at).toLocaleDateString('fr-FR')})</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {r.sent ? 'Envoyee' : 'En attente'}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-amber-700 font-medium">
          Le compromis est signe. Veuillez definir la date limite d'envoi des offres de financement au notaire.
        </p>
      )}
    </div>
  );
};



// ==================== SCENARIOS DE REVENUS LOCATIFS ====================
// Charges annuelles ET budget travaux propres a chaque scenario (copies a la creation, ajustables)
const SC_CHARGE_FIELDS = ['annual_taxes', 'annual_insurance', 'annual_accounting', 'annual_other_charges', 'custom_annual_charges', 'enable_property_management', 'property_management_rate'];
const SC_WORKS_FIELDS = ['works_budget', 'works_items'];
const snapshotRent = (f) => ({
  rental_units: Array.isArray(f.rental_units) ? f.rental_units : [],
  monthly_rent: +f.monthly_rent || 0,
  current_monthly_rent: +f.current_monthly_rent || 0,
  other_income: +f.other_income || 0,
  ...Object.fromEntries([...SC_CHARGE_FIELDS, ...SC_WORKS_FIELDS].map(k => [k, f[k]])),
});
const applyScenarioFields = (f, t) => {
  const out = {
    ...f,
    rental_units: Array.isArray(t.rental_units) ? t.rental_units : [],
    monthly_rent: +t.monthly_rent || 0,
    current_monthly_rent: +t.current_monthly_rent || 0,
    other_income: +t.other_income || 0,
  };
  // Retro-compat : les anciens snapshots n'ont pas charges/travaux -> on garde ceux en cours
  [...SC_CHARGE_FIELDS, ...SC_WORKS_FIELDS].forEach(k => { if (t[k] !== undefined) out[k] = t[k]; });
  return out;
};
const getRentScenarios = (f) => (Array.isArray(f.rent_scenarios) && f.rent_scenarios.length > 0)
  ? f.rent_scenarios
  : [{ id: 'sc_1', name: 'Scénario 1', ...snapshotRent(f) }];
const getActiveScenarioId = (f, scs) => scs.some(s => s.id === f.active_rent_scenario)
  ? f.active_rent_scenario : scs[0].id;
const scenarioRentTotal = (sc) => {
  const units = Array.isArray(sc.rental_units) ? sc.rental_units : [];
  const t = units.reduce((s, u) => s + unitMonthlyRent(u), 0);
  return t > 0 ? t : (+sc.monthly_rent || 0);
};

const RentScenarioBar = ({ form, setForm, live }) => {
  const scs = getRentScenarios(form);
  const activeId = getActiveScenarioId(form, scs);

  const addScenario = () => setForm(f => {
    const cur = getRentScenarios(f);
    const act = getActiveScenarioId(f, cur);
    const saved = cur.map(s => s.id === act ? { ...s, ...snapshotRent(f) } : s);
    const id = 'sc_' + Date.now().toString(36);
    return { ...f, rent_scenarios: [...saved, { id, name: `Scénario ${saved.length + 1}`, ...snapshotRent(f) }], active_rent_scenario: id };
  });

  const switchTo = (id) => setForm(f => {
    const cur = getRentScenarios(f);
    const act = getActiveScenarioId(f, cur);
    if (id === act) return f;
    const saved = cur.map(s => s.id === act ? { ...s, ...snapshotRent(f) } : s);
    const t = saved.find(s => s.id === id);
    if (!t) return f;
    return applyScenarioFields({ ...f, rent_scenarios: saved, active_rent_scenario: id }, t);
  });

  const removeScenario = (id) => {
    if (!window.confirm('Supprimer ce scénario de revenus ?')) return;
    setForm(f => {
      const cur = getRentScenarios(f);
      if (cur.length <= 1) return f;
      const act = getActiveScenarioId(f, cur);
      const rest = cur.filter(s => s.id !== id);
      if (id !== act) return { ...f, rent_scenarios: rest };
      const t = rest[0];
      return applyScenarioFields({ ...f, rent_scenarios: rest, active_rent_scenario: t.id }, t);
    });
  };

  const renameScenario = (id, currentName) => {
    const name = window.prompt('Nom du scénario :', currentName);
    if (!name || !name.trim()) return;
    setForm(f => ({ ...f, rent_scenarios: getRentScenarios(f).map(s => s.id === id ? { ...s, name: name.trim() } : s) }));
  };

  const duplicateScenario = (id) => setForm(f => {
    const cur = getRentScenarios(f);
    const act = getActiveScenarioId(f, cur);
    const saved = cur.map(s => s.id === act ? { ...s, ...snapshotRent(f) } : s);
    const src = saved.find(s => s.id === id);
    if (!src) return f;
    const nid = 'sc_' + Date.now().toString(36);
    const copy = { ...src, id: nid, name: `${src.name || 'Scénario'} (copie)` };
    return applyScenarioFields({ ...f, rent_scenarios: [...saved, copy], active_rent_scenario: nid }, copy);
  });

  const pdfIds = (Array.isArray(form.pdf_scenario_ids) && form.pdf_scenario_ids.length > 0)
    ? form.pdf_scenario_ids.filter(id => scs.some(s => s.id === id))
    : [activeId];
  // Le scenario actif (affiche a l'ecran) est TOUJOURS dans le dossier : il est le scenario principal du PDF
  const effectivePdfIds = pdfIds.includes(activeId) ? pdfIds : [activeId, ...pdfIds];

  const togglePdf = (id) => {
    if (id === activeId) return; // le scenario actif est toujours inclus
    const next = effectivePdfIds.includes(id)
      ? effectivePdfIds.filter(x => x !== id)
      : [...effectivePdfIds, id];
    setForm(f => ({ ...f, pdf_scenario_ids: next }));
  };

  return (
    <div className="mb-4" data-testid="rent-scenario-bar">
      <div className="flex flex-wrap items-center gap-1.5">
        {scs.map((sc, idx) => {
          const isActive = sc.id === activeId;
          const total = isActive ? (live.unitsRentTotal > 0 ? live.unitsRentTotal : (+form.monthly_rent || 0)) : scenarioRentTotal(sc);
          return (
            <div key={sc.id}
                 className={`group flex items-center gap-1.5 rounded-full border px-3 py-1.5 cursor-pointer transition-colors ${isActive ? 'bg-[#008A8D] border-[#008A8D] text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-[#008A8D]/50'}`}
                 onClick={() => switchTo(sc.id)}
                 onDoubleClick={(e) => { e.stopPropagation(); renameScenario(sc.id, sc.name); }}
                 title="Clic : activer · Double-clic : renommer"
                 data-testid={`rent-scenario-${idx}`}>
              <span className="text-xs font-semibold">{sc.name}</span>
              <span className={`text-[10px] ${isActive ? 'text-white/80' : 'text-gray-400'}`}>{fmt(total)} EUR/mois</span>
              <button onClick={(e) => { e.stopPropagation(); duplicateScenario(sc.id); }}
                      className={`text-xs leading-none ${isActive ? 'text-white/70 hover:text-white' : 'text-gray-300 hover:text-[#008A8D]'}`}
                      title="Dupliquer ce scénario (mêmes travaux/charges, à ajuster)"
                      data-testid={`rent-scenario-duplicate-${idx}`}>⧉</button>
              {scs.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); removeScenario(sc.id); }}
                        className={`text-sm leading-none ${isActive ? 'text-white/70 hover:text-white' : 'text-gray-300 hover:text-red-500'}`}
                        title="Supprimer ce scénario"
                        data-testid={`rent-scenario-delete-${idx}`}>×</button>
              )}
            </div>
          );
        })}
        <button onClick={addScenario}
                className="rounded-full border border-dashed border-[#008A8D]/50 text-[#008A8D] text-xs font-semibold px-3 py-1.5 hover:bg-[#008A8D]/5"
                data-testid="add-rent-scenario">+ Scénario</button>
        <button onClick={() => window.dispatchEvent(new CustomEvent('open-scenario-diff'))}
                className="rounded-full bg-[#008A8D] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#0F5C57] shadow"
                title="Ouvrir le tableau comparatif des scénarios"
                data-testid="open-scenario-diff-inline">⇆ Comparer les scénarios</button>
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5">
        Chaque scénario conserve ses lots, loyers, autres revenus, <b>charges annuelles et budget travaux</b> (copiés à la création, ajustables ensuite — honoraires, emprunt et rentabilité recalculés par scénario). ⧉ pour dupliquer (ex. même budget travaux, loyers différents). Double-clic pour renommer.
      </p>
      {scs.length > 1 && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-[#008A8D]/20 bg-[#008A8D]/5 px-3 py-2" data-testid="pdf-scenario-selector">
          <span className="text-[11px] font-semibold text-[#008A8D]">Dossier de présentation PDF — scénarios inclus :</span>
          {scs.map((sc, idx) => (
            <label key={sc.id} className={`flex items-center gap-1.5 text-xs text-gray-700 select-none ${sc.id === activeId ? 'cursor-default' : 'cursor-pointer'}`} data-testid={`pdf-scenario-toggle-${idx}`}>
              <input type="checkbox" checked={effectivePdfIds.includes(sc.id)} onChange={() => togglePdf(sc.id)}
                     disabled={sc.id === activeId}
                     className="accent-[#008A8D] w-3.5 h-3.5 disabled:opacity-60" />
              {sc.name}{sc.id === activeId && <span className="text-[9px] text-[#008A8D] font-bold">(actif — toujours inclus)</span>}
            </label>
          ))}
          <span className="text-[10px] text-gray-400 basis-full">
            {effectivePdfIds.length > 1
              ? 'Le dossier détaille le scénario actif (celui affiché à l\'écran) et ajoute une page comparative avec les autres scénarios cochés.'
              : 'Le dossier est généré uniquement avec le scénario actif (celui affiché à l\'écran), sans page comparative.'}
          </span>
        </div>
      )}
    </div>
  );
};


// ==================== COMPARATIF LOCATION CLASSIQUE vs COURTE DUREE ====================
const LongShortComparison = ({ form, live }) => {
  const units = form.rental_units || [];
  const stUnits = units.filter(u => u && u.short_term);
  if (stUnits.length === 0) return null;
  const missing = stUnits.some(u => !(+u.lt_monthly_rent > 0));
  let classic = null;
  if (!missing) {
    const classicUnits = units.map(u => (u && u.short_term)
      ? { ...u, short_term: false, monthly_rent: +u.lt_monthly_rent || 0 }
      : u);
    classic = computeAnalysisLive({ ...form, rental_units: classicUnits });
  }
  const pct = v => (v || 0).toFixed(2).replace('.', ',') + ' %';
  const other = +form.other_income || 0;
  const rows = classic ? [
    ['Loyers mensuels', fmt(classic.annualRent / 12 - other), fmt(live.annualRent / 12 - other)],
    ['Revenus annuels', fmt(classic.annualRent), fmt(live.annualRent)],
    ['Rendement brut', pct(classic.grossYield), pct(live.grossYield)],
    ['Rendement net', pct(classic.netYield), pct(live.netYield)],
    ['Cashflow mensuel', fmt(classic.monthlyCashflow), fmt(live.monthlyCashflow)],
  ] : [];
  const delta = classic ? (live.monthlyCashflow - classic.monthlyCashflow) : 0;
  return (
    <div className="realease-card p-6" data-testid="lt-st-comparison">
      <h3 className="font-semibold text-gray-900 mb-1">Comparatif : location classique vs courte durée</h3>
      <p className="text-[10px] text-gray-500 mb-2">Même bien, même financement — seuls les loyers des lots en courte durée changent.</p>
      {missing ? (
        <p className="text-xs text-gray-500 italic">Renseignez le <b>loyer équivalent en location classique</b> sur chaque lot en courte durée pour afficher le comparatif.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase text-gray-500">
              <th className="text-left font-semibold pb-1"></th>
              <th className="text-right font-semibold pb-1">Classique</th>
              <th className="text-right font-semibold pb-1 text-[#008A8D]">Courte durée</th>
            </tr></thead>
            <tbody>
              {rows.map(([l, a, b], i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1.5 text-gray-600">{l}</td>
                  <td className="py-1.5 text-right font-semibold text-gray-700">{a}</td>
                  <td className="py-1.5 text-right font-bold text-[#008A8D]">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`mt-3 rounded-lg px-3 py-2 text-xs font-semibold border ${delta >= 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-testid="lt-st-delta">
            {delta >= 0
              ? `La courte durée dégage ${fmt(delta)} EUR / mois de cashflow en plus (${fmt(delta * 12)} EUR / an).`
              : `La location classique dégage ${fmt(-delta)} EUR / mois de cashflow en plus (${fmt(-delta * 12)} EUR / an).`}
          </div>
        </>
      )}
    </div>
  );
};


// ==================== COMPARATEUR MULTI-SCENARIOS ====================
const ScenarioComparator = ({ form, live }) => {
  const [deltaRate, setDeltaRate] = useState(0.5);      // +/- taux
  const [deltaPrice, setDeltaPrice] = useState(0);      // +/- prix cible EUR
  const [deltaRent, setDeltaRent] = useState(0);        // +/- loyer EUR/mois
  const [deltaWorks, setDeltaWorks] = useState(0);      // +/- travaux EUR
  const [duration, setDuration] = useState(form.financing?.loan_duration_years || 25);

  const buildScenario = (rateOffset, priceOffset, rentOffset, worksOffset, dur) => {
    // Works budget effectif = soit somme des works_items, soit works_budget
    const worksBase = (form.works_items && form.works_items.length > 0)
      ? form.works_items.reduce((s, it) => s + (+it.amount || 0), 0)
      : (+form.works_budget || 0);
    const f = {
      ...form,
      target_price: Math.max((+form.target_price || 0) + priceOffset, 0),
      monthly_rent: Math.max((+form.monthly_rent || 0) + rentOffset, 0),
      // On neutralise works_items pour que le scenario utilise works_budget ajuste
      works_items: null,
      works_budget: Math.max(worksBase + worksOffset, 0),
      financing: {
        ...(form.financing || {}),
        interest_rate: Math.max((+form.financing?.interest_rate || 3.65) + rateOffset, 0),
        loan_duration_years: dur,
      },
    };
    return computeAnalysisLive(f);
  };

  const scenarios = useMemo(() => [
    { key: 'opt', label: 'Optimiste', sub: `Taux -${deltaRate}% / +${fmt(deltaRent)} EUR loyer / -${fmt(Math.abs(deltaPrice))} EUR prix`,
      data: buildScenario(-deltaRate, -Math.abs(deltaPrice), Math.abs(deltaRent), -Math.abs(deltaWorks), duration),
      color: 'green' },
    { key: 'central', label: 'Central', sub: 'Hypotheses actuelles',
      data: buildScenario(0, 0, 0, 0, duration),
      color: 'teal' },
    { key: 'prud', label: 'Prudent', sub: `Taux +${deltaRate}% / -${fmt(deltaRent)} EUR loyer / +${fmt(Math.abs(deltaPrice))} EUR prix`,
      data: buildScenario(+deltaRate, +Math.abs(deltaPrice), -Math.abs(deltaRent), +Math.abs(deltaWorks), duration),
      color: 'red' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [form, deltaRate, deltaPrice, deltaRent, deltaWorks, duration]);

  const colorMap = {
    green: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', strong: 'text-green-800' },
    teal:  { bg: 'bg-[#008A8D]/5', border: 'border-[#008A8D]', text: 'text-[#008A8D]', strong: 'text-[#008A8D]' },
    red:   { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', strong: 'text-red-800' },
  };

  return (
    <div className="realease-card p-6" data-testid="scenario-comparator">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Comparateur multi-scenarios</h3>
          <p className="text-xs text-gray-500">Ajustez les variables pour tester la robustesse de l'investissement.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="realease-label">+/- Taux interet (%)</label>
          <input type="number" step="0.1" value={deltaRate} onChange={e => setDeltaRate(+e.target.value || 0)} className="realease-input" data-testid="delta-rate" />
        </div>
        <div>
          <label className="realease-label">+/- Prix cible (EUR)</label>
          <input type="number" step="1000" value={deltaPrice} onChange={e => setDeltaPrice(+e.target.value || 0)} className="realease-input" data-testid="delta-price" />
        </div>
        <div>
          <label className="realease-label">+/- Loyer mensuel (EUR)</label>
          <input type="number" step="10" value={deltaRent} onChange={e => setDeltaRent(+e.target.value || 0)} className="realease-input" data-testid="delta-rent" />
        </div>
        <div>
          <label className="realease-label">+/- Travaux (EUR)</label>
          <input type="number" step="500" value={deltaWorks} onChange={e => setDeltaWorks(+e.target.value || 0)} className="realease-input" data-testid="delta-works" />
        </div>
        <div>
          <label className="realease-label">Duree (annees)</label>
          <select value={duration} onChange={e => setDuration(+e.target.value)} className="realease-input" data-testid="scenario-duration">
            <option value={15}>15 ans</option>
            <option value={20}>20 ans</option>
            <option value={25}>25 ans</option>
            <option value={30}>30 ans</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {scenarios.map(s => {
          const c = colorMap[s.color];
          const cfm = s.data.monthlyCashflow;
          const cfPositive = cfm >= 0;
          return (
            <div key={s.key} className={`p-5 rounded-lg border-2 ${c.bg} ${c.border}`} data-testid={`scenario-${s.key}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-bold ${c.strong}`}>{s.label}</h4>
                <span className={`text-2xl ${cfPositive ? 'text-green-500' : 'text-red-500'}`}>
                  {cfPositive ? '\u2197' : '\u2198'}
                </span>
              </div>
              <p className={`text-xs ${c.text} mb-3 italic`}>{s.sub}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mensualite</span>
                  <span className="font-semibold">{fmt(s.data.monthly.total)} EUR</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cashflow / mois</span>
                  <span className={`font-bold ${cfPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {cfPositive ? '+' : ''}{fmt(cfm)} EUR
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rendement brut</span>
                  <span className="font-semibold">{(s.data.grossYield).toFixed(2)} %</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rendement net</span>
                  <span className="font-semibold">{(s.data.netYield).toFixed(2)} %</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Cout total</span>
                  <span className="font-semibold">{fmt(s.data.totalOperation)} EUR</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
        <p className="text-amber-900">
          <b>Lecture rapide :</b> en scenario prudent, le cashflow varie de
          <b> {fmt(scenarios[2].data.monthlyCashflow - scenarios[1].data.monthlyCashflow)} EUR/mois</b>.
          En scenario optimiste, gain de <b>{fmt(scenarios[0].data.monthlyCashflow - scenarios[1].data.monthlyCashflow)} EUR/mois</b>.
        </p>
      </div>
    </div>
  );
};



// ==================== IMAGES & PLANS MANAGER ====================
const SLOT_LABELS = {
  exterior: { label: 'Photos exterieures', icon: '\ud83c\udfe0', color: 'teal' },
  interior: { label: 'Photos interieures', icon: '\ud83d\udecf', color: 'teal' },
  city: { label: 'Photos de la ville', icon: '\ud83d\uddfa', color: 'amber' },
  before_after: { label: 'Avant / Apres', icon: '\ud83d\udd04', color: 'purple' },
  misc: { label: 'Autres images', icon: '\ud83d\udcc4', color: 'gray' },
};

const ImagesManager = ({ analysisId, levels, onLevelsChange }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [newLevel, setNewLevel] = useState('');

  const fetchImages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/analyses/${analysisId}/images`);
      setImages(r.data || []);
    } catch (e) { console.error(e); }
  }, [analysisId]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('slot', slot);
      await axios.post(`${API}/analyses/${analysisId}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchImages();
    } catch (e) { alert('Erreur upload : ' + (e.response?.data?.detail || e.message)); }
    finally { setUploading(null); }
  };

  const updateImage = async (image_id, updates) => {
    try {
      await axios.put(`${API}/analyses/${analysisId}/images/${image_id}`, updates);
      fetchImages();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.detail || e.message)); }
  };

  const deleteImage = async (image_id) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await axios.delete(`${API}/analyses/${analysisId}/images/${image_id}`);
      fetchImages();
    } catch (e) { alert('Erreur'); }
  };

  const addLevel = () => {
    const lvl = newLevel.trim();
    if (!lvl) return;
    if (levels.includes(lvl)) { alert('Niveau deja existant'); return; }
    onLevelsChange([...levels, lvl]);
    setNewLevel('');
  };

  const removeLevel = (lvl) => {
    if (!window.confirm(`Retirer le niveau ${lvl} ? (les images uploadees pour ce niveau resteront)`)) return;
    onLevelsChange(levels.filter(l => l !== lvl));
  };

  const slotsToShow = [
    ...Object.keys(SLOT_LABELS),
    ...levels.map(l => `level_${l}`),
  ];

  return (
    <div className="realease-card p-6" data-testid="images-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Images & Plans du PDF</h3>
          <p className="text-xs text-gray-500">Uploadez les photos et les plans qui apparaitront dans le PDF.</p>
        </div>
      </div>

      {/* Niveaux */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm font-semibold text-gray-700 mb-2">Niveaux du bien</p>
        <div className="flex flex-wrap items-center gap-2">
          {levels.map(l => (
            <span key={l} className="px-3 py-1 bg-[#008A8D]/10 text-[#008A8D] rounded-full text-sm font-medium flex items-center gap-2">
              {l}
              <button onClick={() => removeLevel(l)} className="text-[#008A8D] hover:text-red-600" data-testid={`remove-level-${l}`}>×</button>
            </span>
          ))}
          <input type="text" value={newLevel} onChange={e => setNewLevel(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addLevel())}
                 placeholder="Ajouter (ex: R+4, Sous-sol)"
                 className="realease-input text-sm w-48" data-testid="new-level-input" />
          <button onClick={addLevel} className="px-3 py-1 bg-[#008A8D] text-white rounded-lg text-sm" data-testid="add-level-btn">+</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slotsToShow.map(slot => {
          const slotImgs = images.filter(i => i.slot === slot);
          const isLevel = slot.startsWith('level_');
          const meta = isLevel
            ? { label: `Plan ${slot.slice(6)}`, icon: '\ud83d\udccd', color: 'blue' }
            : SLOT_LABELS[slot];
          if (!meta) return null;

          return (
            <div key={slot} className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`slot-${slot}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-gray-800">{meta.icon} {meta.label}</h4>
                <span className="text-xs text-gray-400">{slotImgs.length}</span>
              </div>

              {/* Thumbnails */}
              <div className="space-y-2 mb-2">
                {slotImgs.map(img => (
                  <div key={img.image_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <img src={`${API}/analyses/${analysisId}/images/${img.image_id}/raw`}
                         alt={img.title} className="w-12 h-12 object-cover rounded"
                         data-testid={`img-thumb-${img.image_id}`} />
                    <input type="text" value={img.title || ''}
                           onChange={e => {
                             const v = e.target.value;
                             setImages(images.map(x => x.image_id === img.image_id ? { ...x, title: v } : x));
                           }}
                           onBlur={e => updateImage(img.image_id, { title: e.target.value })}
                           className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded"
                           data-testid={`img-title-${img.image_id}`} />
                    <button onClick={() => deleteImage(img.image_id)} className="text-red-500 hover:text-red-700 text-sm" data-testid={`img-delete-${img.image_id}`}>×</button>
                  </div>
                ))}
              </div>

              {/* Upload */}
              <label className="block w-full cursor-pointer">
                <span className="block text-center text-xs text-[#008A8D] border-2 border-dashed border-[#008A8D]/30 rounded-lg py-3 hover:bg-[#008A8D]/5">
                  {uploading === slot ? 'Upload...' : '+ Ajouter'}
                </span>
                <input type="file" accept="image/*" className="hidden"
                       onChange={e => upload(slot, e.target.files?.[0])}
                       data-testid={`upload-${slot}`} />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ==================== TRAVAUX EDITOR ====================
const WorksEditor = ({ form, set }) => {
  const editList = (key, items) => set(key, items);
  const addItem = (key, val) => {
    if (!val.trim()) return;
    editList(key, [...(form[key] || []), val.trim()]);
  };
  const removeItem = (key, idx) => {
    editList(key, (form[key] || []).filter((_, i) => i !== idx));
  };

  const sections = [
    { key: 'works_gros_oeuvre', label: 'Gros oeuvre', placeholder: 'Toiture, Facade, ...' },
    { key: 'works_second_oeuvre', label: 'Second oeuvre', placeholder: 'Electricite, Plomberie, ...' },
    { key: 'works_finitions', label: 'Finitions', placeholder: 'Peinture, Sol, Cuisine, ...' },
  ];

  return (
    <div className="realease-card p-6" data-testid="works-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Travaux & Phases</h3>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Duree prevue :</label>
          <input type="number" min="1" value={form.project_duration_months || ''}
                 onChange={e => set('project_duration_months', +e.target.value || null)}
                 placeholder="9" className="realease-input w-20 text-center" data-testid="project-duration" />
          <span className="text-sm text-gray-600">mois</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sections.map(s => (
          <div key={s.key} className="border border-gray-200 rounded-lg p-3" data-testid={`works-${s.key}`}>
            <h4 className="font-semibold text-sm mb-2 text-[#008A8D]">{s.label}</h4>
            <div className="space-y-1 mb-2">
              {(form[s.key] || []).map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-2 py-1 rounded">
                  <span className="flex-1">• {item}</span>
                  <button onClick={() => removeItem(s.key, i)} className="text-red-500 hover:text-red-700 text-xs">×</button>
                </div>
              ))}
            </div>
            <WorkAdd onAdd={(v) => addItem(s.key, v)} placeholder={s.placeholder} testid={`add-${s.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

const WorkAdd = ({ onAdd, placeholder, testid }) => {
  const [v, setV] = useState('');
  return (
    <div className="flex gap-1">
      <input type="text" value={v} onChange={e => setV(e.target.value)}
             onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), v.trim() && (onAdd(v), setV('')))}
             placeholder={placeholder}
             className="realease-input text-xs flex-1" data-testid={testid} />
      <button onClick={() => { if (v.trim()) { onAdd(v); setV(''); } }} className="px-2 bg-[#008A8D] text-white rounded text-xs">+</button>
    </div>
  );
};


// ==================== RECAP PANEL (sticky right) ====================
const RecapPanel = ({ form, live }) => {
  // Si ENEDIS et Eau sont saisis separement, on les affiche distinctement.
  // Sinon on affiche le total combine "ENEDIS / Eau" (legacy).
  const hasSplit = (+form.enedis_fees || 0) > 0 || (+form.water_fees || 0) > 0;
  const utilitiesItems = hasSplit
    ? [
        { label: 'ENEDIS', value: +form.enedis_fees || 0 },
        { label: 'Eau', value: +form.water_fees || 0 },
      ]
    : [
        { label: 'ENEDIS / Eau', value: +form.enedis_water_fees || 0 },
      ];
  const items = [
    { label: 'Prix foncier', value: +form.target_price || 0 },
    { label: 'Frais de notaire', value: live.notaireFeesAuto },
    { label: "Frais d'agence", value: +form.agency_fees || 0 },
    { label: 'Travaux', value: live.worksBudget },
    ...utilitiesItems,
    { label: 'Mobilier', value: live.furniture },
    { label: 'Honoraires RealEase', value: live.realeaseFee.amount },
  ];
  const total = items.reduce((s, it) => s + (it.value || 0), 0);
  const loanYears = form?.financing?.loan_duration_years || 25;
  const cfMonthly = live?.monthlyCashflow ?? 0;
  const cfAnnual = live?.annualCashflow ?? 0;
  const cfColor = cfMonthly >= 0 ? 'text-emerald-300' : 'text-red-300';
  return (
    <div className="realease-card p-5 bg-gradient-to-br from-[#003E40] to-[#005558] text-white" data-testid="recap-panel">
      <h3 className="font-bold text-base mb-3 flex items-center gap-2">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" clipRule="evenodd"/></svg>
        Recap rapide
      </h3>
      <div className="space-y-1.5 text-sm">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between py-1 border-b border-white/10">
            <span className="text-white/80">{it.label}</span>
            <span className="font-semibold tabular-nums">{fmt(it.value)} EUR</span>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2 mt-2 border-t-2 border-white/40">
          <span className="font-bold">Total operation</span>
          <span className="font-bold text-[#E8DCC4] text-lg tabular-nums">{fmt(total)} EUR</span>
        </div>
      </div>

      {/* Bloc cashflow base sur la duree choisie */}
      <div className="mt-4 pt-3 border-t-2 border-white/30" data-testid="recap-cashflow">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-white/70 mb-1.5">
          <span>Cashflow previsionnel</span>
          <span className="px-2 py-0.5 bg-white/10 rounded-full font-semibold">Sur {loanYears} ans</span>
        </div>
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] text-white/60 uppercase tracking-wide">Mensuel net</p>
            <p className={`text-2xl font-bold tabular-nums ${cfColor}`}>{cfMonthly >= 0 ? '+' : ''}{fmt(cfMonthly)} EUR</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-white/60 uppercase tracking-wide">Annuel net</p>
            <p className={`text-base font-semibold tabular-nums ${cfColor}`}>{cfAnnual >= 0 ? '+' : ''}{fmt(cfAnnual)} EUR</p>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== CONVERT TO PROJECT BUTTON ====================
const ConvertToProjectBtn = ({ analysisId, currentProjectId }) => {
  const [open, setOpen] = React.useState(false);
  const [leads, setLeads] = React.useState([]);
  const [leadId, setLeadId] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const openDialog = async () => {
    if (currentProjectId) {
      window.location.href = `/projects/${currentProjectId}`;
      return;
    }
    try {
      const r = await axios.get(`${API}/crm/leads`);
      setLeads(r.data || []);
      setOpen(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur chargement leads');
    }
  };

  const submit = async () => {
    if (!leadId) { alert("Choisissez un client qualifie"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/analyses/${analysisId}/convert-to-project`, { lead_id: leadId });
      window.location.href = `/projects/${r.data.project_id}`;
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur conversion');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={openDialog}
              className="px-4 py-2 bg-[#003E40] text-white text-sm font-semibold rounded-lg hover:bg-[#005558] flex items-center gap-2"
              data-testid="convert-to-project-btn">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        {currentProjectId ? 'Voir le projet lie' : 'Convertir en projet client'}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="convert-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Convertir en projet client</h2>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Cree un projet client lie a cette analyse. Le dossier de financement sera pre-rempli automatiquement (revenus, loyers, ...).
            </p>
            <label className="realease-label">Attribuer a un client qualifie *</label>
            <select value={leadId} onChange={e => setLeadId(e.target.value)} className="realease-input mb-4" data-testid="convert-lead-select">
              <option value="">Choisir un client...</option>
              {leads.map(l => (
                <option key={l.lead_id} value={l.lead_id}>
                  {l.first_name} {l.last_name} {l.email ? `(${l.email})` : ''}
                </option>
              ))}
            </select>
            <div className="flex gap-3">
              <button onClick={() => setOpen(false)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Annuler</button>
              <button onClick={submit} disabled={busy || !leadId} className="flex-1 realease-btn-primary disabled:opacity-50" data-testid="convert-submit">
                {busy ? 'Creation...' : 'Creer le projet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// ==================== MARKET RATES BUTTON (meilleurtaux) ====================
const MarketRatesBtn = ({ duration, onPick }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const fetchRates = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/market/interest-rates${refresh ? '?refresh=true' : ''}`);
      setData(r.data);
      setOpen(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur recuperation taux');
    } finally { setLoading(false); }
  };

  const pickRate = (years) => {
    const v = data?.rates?.[String(years)];
    if (v != null) {
      onPick(v);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button onClick={() => fetchRates(false)} disabled={loading}
              className="px-3 py-1.5 text-xs bg-[#E8DCC4] text-[#1A1A1A] rounded-lg hover:bg-[#dcc9a8] font-semibold disabled:opacity-50 flex items-center gap-1.5"
              data-testid="market-rates-btn">
        {loading ? '...' : 'Taux du marche (meilleurtaux)'}
      </button>
      {open && data && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-20 p-4" data-testid="market-rates-popover">
          <div className="flex justify-between items-center mb-2">
            <p className="font-semibold text-sm text-gray-800">Taux moyens marche</p>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Source : <b>{data.source}</b>
            {data.from_cache && <span className="text-amber-600"> (cache)</span>}
            {data.fallback && <span className="text-red-600"> (fallback)</span>}
          </p>
          <div className="space-y-1.5">
            {[15, 20, 25].map(y => {
              const v = data.rates?.[String(y)];
              const isSelected = y === duration;
              return (
                <button key={y} onClick={() => pickRate(y)} disabled={v == null}
                        className={`w-full flex justify-between items-center p-2 rounded text-sm hover:bg-[#008A8D]/10 transition-colors disabled:opacity-40 ${isSelected ? 'bg-[#008A8D]/5 border border-[#008A8D]/30' : ''}`}
                        data-testid={`pick-rate-${y}`}>
                  <span>Sur {y} ans {isSelected && <span className="text-[10px] text-[#008A8D] ml-1">(votre duree)</span>}</span>
                  <span className="font-bold text-[#008A8D]">{v != null ? `${v.toFixed(2)} %` : '—'}</span>
                </button>
              );
            })}
          </div>
          <button onClick={() => fetchRates(true)} className="mt-3 w-full text-xs text-gray-500 hover:text-[#008A8D]" data-testid="refresh-rates">
            Forcer rafraichissement
          </button>
        </div>
      )}
    </div>
  );
};


// ==================== CITY SECTION EDITOR ====================
const CitySection = ({ form, set, analysisId }) => {
  const highlights = form.city_highlights || [];
  const [generating, setGenerating] = React.useState(false);
  const [autoHint, setAutoHint] = React.useState(false);

  const generateAuto = async (silent = false) => {
    if (!form.city) {
      if (!silent) alert("Renseignez d'abord la ville");
      return;
    }
    if (!silent && !window.confirm(`Generer automatiquement les atouts et l'etude de marche pour ${form.city}${form.postal_code ? ' (' + form.postal_code + ')' : ''} ? (45-90 sec, IA)`)) return;
    setGenerating(true);
    try {
      const params = new URLSearchParams({
        force: silent ? 'false' : 'true',
        city: form.city || '',
        ...(form.postal_code ? { postal_code: form.postal_code } : {}),
      });
      const r = await axios.post(`${API}/analyses/${analysisId}/auto-fill-city?${params.toString()}`);
      set('city_pitch', r.data.city_pitch || form.city_pitch);
      set('city_highlights', r.data.city_highlights || form.city_highlights);
      set('market_analysis', r.data.market_analysis || form.market_analysis);
      setAutoHint(false);
    } catch (e) {
      if (!silent) alert(e.response?.data?.detail || 'Erreur generation');
    } finally { setGenerating(false); }
  };

  // Auto-declenchement : quand ville+CP sont renseignes ET que les champs IA sont vides,
  // on propose une generation automatique (avec un delai de 2.5s pour ne pas spammer pendant la saisie).
  React.useEffect(() => {
    if (!form.city || !form.postal_code) return;
    const already = (form.city_pitch || '').trim() || (form.market_analysis || '').trim();
    if (already) return;
    const t = setTimeout(() => setAutoHint(true), 1500);
    return () => clearTimeout(t);
  }, [form.city, form.postal_code, form.city_pitch, form.market_analysis]);

  return (
    <div className="realease-card p-6" data-testid="city-section-editor">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900">Section "La ville" (PDF)</h3>
        <button onClick={() => generateAuto(false)} disabled={generating}
                className="px-3 py-1.5 text-xs bg-gradient-to-br from-[#008A8D] to-[#006B6E] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="auto-fill-city-btn">
          {generating ? (
            <><span className="animate-spin inline-block w-3 h-3 border-2 border-t-transparent border-white rounded-full"></span>Generation...</>
          ) : (
            <>Auto-remplir avec IA</>
          )}
        </button>
      </div>
      {/* Proposition automatique si ville+CP saisis et sections vides */}
      {autoHint && !generating && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-center justify-between gap-3 flex-wrap" data-testid="auto-city-suggest">
          <p className="text-sm text-amber-900 flex-1">
            <b>{form.city}</b> ({form.postal_code}) &mdash; Voulez-vous generer <b>automatiquement</b> le pitch + les atouts + l'etude marche via IA ? (45-90 sec)
          </p>
          <div className="flex gap-2">
            <button onClick={() => generateAuto(true)}
                    className="px-3 py-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded font-semibold"
                    data-testid="auto-city-accept">Oui, generer</button>
            <button onClick={() => setAutoHint(false)}
                    className="px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-100 rounded">Plus tard</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label className="realease-label">Texte de presentation (charme du secteur, attractivite, etc.)</label>
          <textarea value={form.city_pitch || ''} onChange={e => set('city_pitch', e.target.value)}
                    className="realease-input h-28" data-testid="city-pitch"
                    placeholder="Ex: Carhaix-Plouguer est une commune dynamique au coeur du Centre-Bretagne..." />
        </div>
        <div>
          <label className="realease-label">Atouts du secteur (un par ligne)</label>
          <textarea
            value={highlights.join('\n')}
            onChange={e => set('city_highlights', e.target.value.split('\n').map(x => x.trim()).filter(Boolean))}
            className="realease-input h-24" data-testid="city-highlights"
            placeholder={"Ecoles a moins de 5 min\nGare TER a 800 m\nMarche couvert deux fois par semaine"}
          />
        </div>
        <div>
          <label className="realease-label">Etude de marche approfondie (PDF — page dediee)</label>
          <textarea value={form.market_analysis || ''} onChange={e => set('market_analysis', e.target.value)}
                    className="realease-input h-40" data-testid="market-analysis"
                    placeholder="Evolution des prix, rendement locatif moyen, profil de la demande, quartiers attractifs, projets d'urbanisme, risques..." />
          <p className="text-xs text-gray-400 mt-1">Cette section ajoute une page complete au PDF avec une analyse approfondie du marche local.</p>
        </div>
      </div>
    </div>
  );
};


// ==================== TIMELINE EDITOR ====================
const DEFAULT_TIMELINE = [
  { label: 'Promesse de vente', done: true },
  { label: 'Obtention financement' },
  { label: 'Signature acte authentique' },
  { label: 'Travaux' },
  { label: 'Mise en location' },
];

const TimelineEditor = ({ form, set }) => {
  const steps = form.timeline_steps || DEFAULT_TIMELINE;

  const update = (i, field, value) => {
    const ns = [...steps];
    ns[i] = { ...ns[i], [field]: value };
    set('timeline_steps', ns);
  };
  const add = () => set('timeline_steps', [...steps, { label: 'Nouvelle etape' }]);
  const remove = (i) => set('timeline_steps', steps.filter((_, idx) => idx !== i));

  return (
    <div className="realease-card p-6" data-testid="timeline-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">Calendrier du projet</h3>
          <p className="text-xs text-gray-500">Etapes affichees sur la derniere page du PDF.</p>
        </div>
        <button onClick={add} className="text-sm text-[#008A8D] hover:underline" data-testid="add-timeline-step">+ Etape</button>
      </div>
      <div className="space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`timeline-row-${i}`}>
            <input type="text" value={s.label || ''} onChange={e => update(i, 'label', e.target.value)}
                   className="realease-input col-span-5 text-sm" placeholder="Etape" />
            <input type="date" value={s.date || ''} onChange={e => update(i, 'date', e.target.value)}
                   className="realease-input col-span-3 text-sm" />
            <select value={s.done ? 'done' : (s.status || 'pending')}
                    onChange={e => update(i, e.target.value === 'done' ? 'done' : 'status',
                                            e.target.value === 'done' ? true : e.target.value)}
                    className="realease-input col-span-3 text-sm">
              <option value="pending">A venir</option>
              <option value="in_progress">En cours</option>
              <option value="done">Termine</option>
            </select>
            <button onClick={() => remove(i)} className="col-span-1 text-red-500 hover:text-red-700">×</button>
          </div>
        ))}
      </div>
    </div>
  );
};
